import { consume } from "@lit/context";
import { Task } from "@lit/task";
import {
    Absence,
    AbsenceStatus,
    type AutoAssignCriteria,
    Availability,
    AvailabilityStatus,
    type DailyWeatherPrediction,
    type DateRange,
    type Department,
    type Employee,
    GetAbsencesParams,
    GetAvailabilitiesParams,
    GetDailyResultsParams,
    GetPublicRosterUrlParams,
    GetRosterNotesParams,
    GetRosterTargetsParams,
    GetSchoolHolidaysParams,
    GetWeatherPredictionsParams,
    type Holiday,
    type Issue,
    type Position,
    type RosterDailyResultsDTO,
    type RosterNoteDTO,
    type RosterTab,
    type RosterTargets,
    RosterTemplate,
    type SchoolHoliday,
    type ShiftTemplate,
    TimeEntry,
    TimeEntryType,
    UpdateVenueParams,
    applyPastDateFlip,
    assignEmployee,
    createRosterNote,
    dateAdd,
    formatDate,
    formatDateShort,
    formatWeekDay,
    formatWeekDayShort,
    getAllDepartmentPositions,
    getHolidayForDate,
    getHolidaysInRange,
    getRange,
    getShiftIssues,
    getShiftsForIssue,
    inferRangeType,
    matchesFilters,
    matchesTimeFilter,
    moveTimesToDate,
    parseTimes,
    timeEntryTypeColor,
    toDateString,
    toDurationString,
    wait,
} from "@pentacode/core";
import type { DateString, Hours } from "@pentacode/openapi";
import { activeIcon } from "@pentacode/ui/components";
import { LitElement, css, html } from "lit";
import { customElement, query, queryAll, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { keyed } from "lit/directives/keyed.js";
import { live } from "lit/directives/live.js";
import { until } from "lit/directives/until.js";
import "../../components/avatar/avatar";
import "../../components/roster-notes/roster-note-list";
import type { RosterNotes } from "../../components/roster-notes/roster-note-list";
import { type EnabledFeatures, enabledFeatures } from "../../contexts/features-context";
import "../../elements/absence-dialog";
import type { AbsenceDialog } from "../../elements/absence-dialog";
import { alert, confirm } from "../../elements/alert-dialog";
import "../../elements/availability-dialog";
import type { AvailabilityDialog } from "../../elements/availability-dialog";
import { Checkbox } from "../../elements/checkbox";
import "../../elements/date-range-picker";
import "../../elements/dialog";
import type { Dialog } from "../../elements/dialog";
import "../../elements/employee-day";
import type { EmployeeDay } from "../../elements/employee-day";
import "../../elements/issue";
import "../../elements/popover";
import type { Popover } from "../../elements/popover";
import { type RosterEntry, entryPositions } from "../../elements/roster-entry";
import "../../elements/scroller";
import "../../elements/send-message-dialog";
import type { SendMessageDialog } from "../../elements/send-message-dialog";
import { app, appMode } from "../../init";
import { print } from "../../lib/print";
import { singleton } from "../../lib/singleton";
import { debounce, isCursorInInput, isSafari } from "../../lib/util";
import { Routing, routeProperty } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { colors, shared } from "../../styles";
import "./components/auto-assign-menu";
import type { AutoAssignMenu } from "./components/auto-assign-menu";
import "./components/create-roster-template-dialog";
import type { CreateRosterTemplateDialog } from "./components/create-roster-template-dialog";
import "./components/publish-roster-dialog";
import type { PublishRosterDialog } from "./components/publish-roster-dialog";
import "./components/roster-changes";
import { RosterCosts } from "./components/roster-costs";
import "./components/roster-row";
import type { RosterRow } from "./components/roster-row";
import "./components/roster-tabs";
import { RosterTargetsElement } from "./components/roster-targets";
import "./components/weather";
import { filterClearableEntries } from "./lib/clear-roster";
import { isDayReadonly } from "./lib/day-readonly";
import { renderDayWindow } from "./lib/day-window";
import { calculateFrequentTimes } from "./lib/frequent-times";
import type { Direction } from "./lib/roster-navigation";
import { groupUnassignedStacks } from "./lib/roster-navigation";
import { RosterNavigation } from "./lib/roster-navigation-controller";
import { findEmployeeRow } from "./lib/roster-row-lookup";
import { calculateTimeBalance } from "./lib/time-balance";
import type { DayData, DepartmentData, DragData, EmployeeData, RosterData } from "./types";

// Reactive properties that a cursor move (WASD / click) changes — and nothing else. An
// update whose changed keys are all in this set can't affect layout geometry, so geometry
// reads (e.g. the department-header restyle) can be skipped for it.
const CURSOR_STATE_PROPS = new Set([
    "_activeDate",
    "_activeEmployee",
    "_activeDepartment",
    "_activeEntry",
    "_activeField",
]);

const LAYOUT_NEUTRAL_PROPS = new Set([...CURSOR_STATE_PROPS, "_dragData", "_colWindow"]);

function changesLimitedTo(changes: Map<string, unknown>, props: Set<string>) {
    if (changes.size === 0) {
        return false;
    }
    for (const key of changes.keys()) {
        if (!props.has(key)) {
            return false;
        }
    }
    return true;
}

function isCursorOnlyChange(changes: Map<string, unknown>) {
    return changesLimitedTo(changes, CURSOR_STATE_PROPS);
}

@customElement("ptc-roster")
export class Roster extends Routing(StateMixin(LitElement)) {
    routePattern = /^roster/;

    get routeTitle() {
        return this._venue ? `Dienstplan - ${this._venue.name}` : "Dienstplan";
    }

    get helpPage() {
        return "/handbuch/dienstplan";
    }

    protected defaultRange = () => getRange(new Date(), "week");
    protected rangeChanged = () => this.synchronize(true);

    private get _venue() {
        return this._activeTab && app.getVenue(this._activeTab.venue);
    }

    @routeProperty({ type: Number, param: "tab", default: () => 0 })
    private _activeTabIndex: number;

    @routeProperty({ type: Number, param: "employee" })
    private _activeEmployeeId?: number;

    // Disambiguates the row when the same employee appears under more than one department.
    @routeProperty({ type: Number, param: "department" })
    private _activeDepartmentId?: number;

    @routeProperty({ type: Number, param: "rosterTemplate" })
    private _rosterTemplateId?: number;

    private get _activeTab(): RosterTab | undefined {
        const rosterTabs = app.rosterTabs;

        const clampedIndex = Math.min(rosterTabs.length - 1, Math.max(0, this._activeTabIndex));
        return rosterTabs[clampedIndex];
    }

    @consume({ context: enabledFeatures, subscribe: true })
    private _enabledFeatures: EnabledFeatures;

    @state()
    private _employees: EmployeeData[] = [];

    @state()
    private _data: RosterData;

    @state()
    private _filterString = "";

    @state()
    private _timeEntries: TimeEntry[] = [];

    @state()
    private _previousTimeEntries: TimeEntry[] = [];

    @state()
    private _dailyResults: RosterDailyResultsDTO[] = [];

    @state()
    private _issues: Issue[] = [];

    @state()
    private _loading: boolean = false;

    @state()
    private _previewRosterTemplate: RosterTemplate | null = null;

    @state()
    private _previewTimeEntries: TimeEntry[] | null = null;

    @state()
    private _targets: RosterTargets[] = [];

    @state()
    private _absences: Absence[] = [];

    @state()
    private _availabilities: Availability[] = [];

    @state()
    private _activeDate: DateString | null = null;

    @state()
    private _activeEmployee: number | null = null;

    @state()
    private _activeDepartment: number | null = null;

    @state()
    private _activeEntry: string | null = null;

    @state()
    private _activeField: string | null = null;

    @state()
    private _displayAutoAssignMenu = false;

    @state()
    private _dragData: DragData | null = null;

    private _isCopyDrag = false;

    // Horizontal (column) virtualization, large ranges only. _colWindow is the [start,end) range
    // of day columns to render; every per-day row windows to it (see RosterRow). _dayColWidthPx is
    // the measured px width of one day column, used for the window math and the spacer sizes.
    @state()
    private _colWindow: { start: number; end: number } | null = null;
    private _dayColWidthPx = 0;
    private _colScrollRaf = 0;
    private _colScrollAttached = false;

    @state()
    private _rosterNotes: RosterNoteDTO[] = [];

    @state()
    private _schoolHolidays: SchoolHoliday[] = [];

    @state()
    private _publicUrlPromise: Promise<string> = new Promise<string>(() => {});

    @state()
    private _frequentTimes: ShiftTemplate[] = [];

    @state()
    private _collapsed = new Set<number>();

    @state()
    private _collapsedSuggestions = new Set<number>();

    @state()
    private _weatherPredictions: Promise<DailyWeatherPrediction[]> = Promise.resolve([]);

    @state()
    private _entryHeight = app.settings.rosterDisplayMode === "regular" ? 3.5 : 2.1;

    @query(".main")
    private _main: HTMLDivElement;

    @query("#filterInput")
    private _filterInput: HTMLInputElement;

    @query("#rosterTemplatesPopover")
    private _rosterTemplatesPopover: Popover;

    @query("#clearRosterDialog")
    private _clearRosterDialog: Dialog<void, void>;

    @query("ptc-employee-day")
    private _employeeDayForm: EmployeeDay;

    @query("#issuePopover")
    private _issuePopover: Popover;

    @queryAll("ptc-roster-targets")
    private _rosterTargetsElements: RosterTargetsElement[];

    @query("ptc-roster-costs")
    private _rosterCosts: RosterCosts;

    @query("ptc-roster-notes")
    private _rosterNotesEl: RosterNotes;

    @query("ptc-auto-assign-menu")
    private _autoAssignMenu: AutoAssignMenu;

    @singleton("ptc-publish-roster-dialog")
    private _publishRosterDialog: PublishRosterDialog;

    @singleton("ptc-absence-dialog")
    private _absenceDialog: AbsenceDialog;

    @singleton("ptc-send-message-dialog")
    private _sendMessageDialog: SendMessageDialog;

    @singleton("ptc-availability-dialog")
    private _availabilityDialog: AvailabilityDialog;

    @singleton("ptc-create-roster-template-dialog")
    private _createRosterTemplateDialog: CreateRosterTemplateDialog;

    private _resizeObserver?: ResizeObserver;

    private get _departments(): Department[] {
        return this._venue?.departments.filter((department) => app.hasAccess({ department })) || [];
    }

    private get _filteredDepartments(): Department[] {
        return this._departments.filter(
            (d) => !this._activeTab?.departments || this._activeTab.departments.includes(d.id)
        );
    }

    /** This tasks generates a new url with the parameters that have to be signed by the token */
    private _publicRosterUrlTask: Task<ReadonlyArray<unknown>, string | undefined> = new Task(this, {
        task: async ([venueId, from, to, departments]) => {
            if (!venueId || !from || !to) {
                return undefined;
            }

            const response = await app.api.getPublicRosterUrl(
                new GetPublicRosterUrlParams({
                    venue: venueId,
                    from,
                    to,
                    departments: departments.length ? departments : undefined,
                })
            );

            return response.url;
        },
        argsEqual(oldArgs, newArgs) {
            return JSON.stringify(oldArgs) === JSON.stringify(newArgs);
        },
        args: () => [this._venue?.id, this.dateFrom, this.dateTo, this._filteredDepartments.map((d) => d.id)] as const,
    });

    /** This tasks takes a generated url and changes the parameters that don't affect the token */
    private _publicRosterUrlWithFiltersTask: Task<ReadonlyArray<unknown>, string | undefined> = new Task(this, {
        task: async ([url, mirrorDepartments]) => {
            if (!url) {
                return undefined;
            }

            if (mirrorDepartments) {
                return `${url}&md=1`;
            }

            return url;
        },
        args: () => [this._publicRosterUrlTask.value, app.settings.rosterMirrorDepartments] as const,
    });

    private get _unpublishedEntries() {
        if (!this.dateRange) {
            return [];
        }
        const { from, to } = this.dateRange;
        const positions = this._filteredDepartments.flatMap((d) => getAllDepartmentPositions(d).map((p) => p.id));
        return this._timeEntries.filter(
            (e) =>
                (e.position
                    ? positions.includes(e.position.id)
                    : e.employeeId
                      ? app.getEmployee(e.employeeId)?.positions.some((p) => positions.includes(p.id))
                      : true) &&
                e.date >= from &&
                e.date < to &&
                !e.isPast &&
                (!this._activeTab?.time || e.matchesTimeFilter(this._activeTab.time)) &&
                !e.isPublished
        );
    }

    private get _unpublishedCount() {
        return this._unpublishedEntries.length;
    }

    private get _filteredRosterNotes() {
        if (!this._activeTab) {
            return this._rosterNotes;
        }

        const departments = this._filteredDepartments;

        return this._rosterNotes.filter((note) =>
            departments.some((department) => matchesFilters(note.filters, { company: app.company, department }))
        );
    }

    private _keydownHandler = (e: KeyboardEvent) => {
        if (
            !this.active ||
            e.ctrlKey ||
            e.metaKey ||
            (!this._employeeDayForm?.matches(":focus-within") && isCursorInInput()) ||
            (this._displayAutoAssignMenu && e.key !== "Escape")
        ) {
            return;
        }
        const direction = { w: "up", s: "down", a: "left", d: "right" }[e.key] as Direction;
        if (e.shiftKey && e.key.toLowerCase() === "a") {
            this.go(null, { ...this.router.params, date: dateAdd(this.date, { days: -7 }) });
            if (this._activeDate) {
                this._setActive({ date: dateAdd(this._activeDate, { days: -7 }) });
            }
        } else if (e.shiftKey && e.key.toLowerCase() === "d") {
            this.go(null, { ...this.router.params, date: dateAdd(this.date, { days: 7 }) });
            if (this._activeDate) {
                this._setActive({ date: dateAdd(this._activeDate, { days: 7 }) });
            }
        } else if (direction) {
            this._navigation.moveCursor(direction);
            e.preventDefault();
        } else if (e.key === "Escape") {
            if (this._displayAutoAssignMenu) {
                this._autoAssignMenu?.close();
            }
            this._setActive({ ...this.router.params, date: null, employee: null, department: null, entry: null }, true);
        } else if (e.shiftKey && e.key === "Backspace") {
            const activeEntry = this._timeEntries.find((e) => e.id === this._activeEntry);
            if (activeEntry) {
                void this._removeTimeEntry(activeEntry);
            }
        } else if (e.key === "n") {
            this._setActive({ entry: "new" });
        }
    };

    private _dragoverHandler = (e: DragEvent) => this._dragover(e);
    private _dropHandler = (e: DragEvent) => e.preventDefault();
    private _dragendHandler = (e: DragEvent) => this._dragend(e);

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener("dragover", this._dragoverHandler);
        this.addEventListener("drop", this._dropHandler);
        this.addEventListener("dragend", this._dragendHandler);
        document.addEventListener("keydown", this._keydownHandler);
        app.onTimeEntriesSynced(this._timeEntriesSyncedHandler);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener("dragover", this._dragoverHandler);
        this.removeEventListener("drop", this._dropHandler);
        this.removeEventListener("dragend", this._dragendHandler);
        document.removeEventListener("keydown", this._keydownHandler);
        app.offTimeEntriesSynced(this._timeEntriesSyncedHandler);
        clearTimeout(this._balanceRefetchTimeout);
        this._main?.removeEventListener("scroll", this._colScrollHandler);
        this._colScrollAttached = false;
        cancelAnimationFrame(this._colScrollRaf);
        cancelAnimationFrame(this._restoreScrollRaf);
        this._rowsObserver?.disconnect();
        this._rowsObserver = undefined;
        this._departmentsObserver?.disconnect();
        this._departmentsObserver = undefined;
        this._resizeObserver?.disconnect();
        this._resizeObserver = undefined;
        cancelAnimationFrame(this._scrollRaf);
        entryPositions.clear();
    }

    // Owns the navigation-grid cache + pure movement math (see ./lib/roster-navigation*).
    private _navigation = new RosterNavigation({
        data: () => this._data,
        filterString: () => this._filterString,
        collapsed: () => this._collapsed,
        dateRange: () => this.dateRange,
        visibleDepartmentIds: () => {
            const ids = this._activeTab?.departments;
            return ids ? new Set(ids) : null;
        },
        includeUnassigned: () => app.settings.rosterDisplayUnassigned && app.settings.rosterDisplayMode !== "minimal",
        activeCell: () => ({
            date: this._activeDate,
            employee: this._activeEmployee,
            department: this._activeDepartment,
            entry: this._activeEntry,
        }),
        setActive: (cell) => this._setActive(cell, true),
    });

    private _rowsObserver?: IntersectionObserver;
    private _departmentsObserver?: IntersectionObserver;

    private _rowsIntersectionHandler(entries: IntersectionObserverEntry[]) {
        entries.forEach((e) => ((e.target as RosterRow).isVisible = e.isIntersecting));
    }

    private _departmentsIntersectionHandler(entries: IntersectionObserverEntry[]) {
        entries.forEach((e) => {
            const id = Number((e.target as HTMLDivElement).dataset.department);
            if (e.isIntersecting) {
                this._collapsedSuggestions.delete(id);
            } else {
                this._collapsedSuggestions.add(id);
            }
        });
        this.requestUpdate();
    }

    private _observeRows() {
        if (!this._rowsObserver) {
            const main = this.renderRoot.querySelector(".main");
            if (!main) {
                return;
            }
            this._rowsObserver = new IntersectionObserver(
                (entries: IntersectionObserverEntry[]) => this._rowsIntersectionHandler(entries),
                { root: main, rootMargin: "50% 100000px" }
            );
        }
        const elements = this.renderRoot.querySelectorAll("ptc-roster-row");
        for (const el of elements) {
            this._rowsObserver.observe(el);
        }
    }

    private _observeDepartments() {
        if (!this._departmentsObserver) {
            const main = this.renderRoot.querySelector(".main");
            if (!main) {
                return;
            }
            this._departmentsObserver = new IntersectionObserver(
                (entries: IntersectionObserverEntry[]) => this._departmentsIntersectionHandler(entries),
                { root: main, rootMargin: "-110px" }
            );
        }
        const elements = this.renderRoot.querySelectorAll(".department");
        for (const el of elements) {
            this._departmentsObserver.observe(el);
        }
    }

    private _colScrollHandler = () => {
        // Recompute the column window at most once per frame; vertical scroll leaves scrollLeft
        // unchanged so the window stays put (and re-render is index-gated below).
        if (this._colScrollRaf) {
            return;
        }
        this._colScrollRaf = requestAnimationFrame(() => {
            this._colScrollRaf = 0;
            this._recomputeColWindow();
        });
    };

    private _recomputeColWindow() {
        const main = this._main;
        if (!main || !this._data || this._data.dates.length <= 9 || !this._dayColWidthPx) {
            if (this._colWindow) {
                this._colWindow = null;
            }
            return;
        }
        const colW = this._dayColWidthPx;
        const total = this._data.dates.length;
        const visible = Math.ceil(main.clientWidth / colW);
        const first = Math.floor(main.scrollLeft / colW);
        // ~1 viewport of buffer on each side (see plan): window ≈ 3 viewports wide.
        const start = Math.max(0, first - visible);
        const end = Math.min(total, first + visible * 2);
        if (!this._colWindow || this._colWindow.start !== start || this._colWindow.end !== end) {
            this._colWindow = { start, end };
        }
    }

    private _lastColMode = "";

    // Horizontal scroll position carried across a date-range change. Stored as a fraction of the
    // scrollable width so it survives a change in the number of day columns (different range length),
    // captured from the old layout before the new data renders and re-applied after.
    private _pendingScrollProgress: number | null = null;
    private _restoreScrollRaf = 0;

    private _captureScrollProgress() {
        const main = this._main;
        if (!main) {
            return;
        }
        const max = main.scrollWidth - main.clientWidth;
        this._pendingScrollProgress = max > 0 ? main.scrollLeft / max : 0;
    }

    private _restoreScrollProgress() {
        const progress = this._pendingScrollProgress;
        this._pendingScrollProgress = null;
        if (progress == null) {
            return;
        }
        // The new range's scroll width only settles after the re-keyed rows recreate and re-layout,
        // so apply the offset on the next frame against the final scroll width — applying it inline
        // would map the fraction onto the stale (old-range) width and then get clamped.
        cancelAnimationFrame(this._restoreScrollRaf);
        this._restoreScrollRaf = requestAnimationFrame(() => {
            const main = this._main;
            if (!main) {
                return;
            }
            const max = main.scrollWidth - main.clientWidth;
            // "instant" overrides .main's CSS smooth scroll-behavior so the position is restored as a
            // jump, not an animated scroll.
            main.scrollTo({ left: max > 0 ? Math.round(progress * max) : 0, behavior: "instant" as ScrollBehavior });
            this._recomputeColWindow();
        });
    }

    private _syncColumnWindow() {
        const main = this._main;
        const isLarge = !!this._data && this._data.dates.length > 9;
        if (!main || !isLarge) {
            if (this._colWindow) {
                this._colWindow = null;
            }
            return;
        }
        // Re-measure the column width only on first run or a display-mode change, never per render
        // (a .day-header offsetWidth read forces a full-tree layout — too costly to do every update).
        const mode = app.settings.rosterDisplayMode;
        if (mode !== this._lastColMode) {
            this._lastColMode = mode;
            this._dayColWidthPx = 0;
        }
        if (!this._dayColWidthPx) {
            const cell = this.renderRoot.querySelector<HTMLElement>(".day-header");
            if (cell?.offsetWidth) {
                this._dayColWidthPx = cell.offsetWidth;
            }
        }
        if (!this._colScrollAttached) {
            this._colScrollAttached = true;
            main.addEventListener("scroll", this._colScrollHandler, { passive: true });
        }
        this._recomputeColWindow();
    }

    // Set by the cursor-move fast path so _setActive doesn't schedule a second scroll.
    private _cursorMoveHandled = false;

    shouldUpdate(changes: Map<string, unknown>) {
        if (!super.shouldUpdate(changes)) {
            return false;
        }
        if (!isCursorOnlyChange(changes)) {
            return true;
        }
        // A cursor move between two real cells only changes the row highlights + side panel, so
        // push that imperatively and skip the ~80-row parent render. Transitions to/from "no active
        // cell" (incl. unassigned, which has no employee) still full-render — the panel swaps.
        const prevDate = (
            changes.has("_activeDate") ? changes.get("_activeDate") : this._activeDate
        ) as DateString | null;
        const prevEmployee = (
            changes.has("_activeEmployee") ? changes.get("_activeEmployee") : this._activeEmployee
        ) as number | null;
        const prevDepartment = (
            changes.has("_activeDepartment") ? changes.get("_activeDepartment") : this._activeDepartment
        ) as number | null;
        const hadCell = !!(prevDate && prevEmployee && prevDepartment);
        const hasCell = !!(this._activeDate && this._activeEmployee && this._activeDepartment);
        if (!hadCell || !hasCell) {
            return true;
        }
        this._applyCursorMove(prevDepartment, prevEmployee);
        return false;
    }

    private _findRosterRow(departmentId: number | null, employeeId: number | null): RosterRow | undefined {
        if (!departmentId) {
            return undefined;
        }
        // Unassigned cell: its row is the lone ptc-roster-row in the department's .unassigned-row.
        if (!employeeId) {
            return (
                (this.renderRoot.querySelector(
                    `.department[data-department="${departmentId}"] .unassigned-row ptc-roster-row`
                ) as RosterRow | null) ?? undefined
            );
        }
        const rows = this.renderRoot.querySelectorAll("ptc-roster-row") as NodeListOf<RosterRow>;
        for (const row of rows) {
            if (row.department?.department?.id === departmentId && row.employee?.employee?.id === employeeId) {
                return row;
            }
        }
        return undefined;
    }

    // Only reached via shouldUpdate's hasCell fast path, so _activeDate/_activeDepartment are
    // non-null here (hence the `!` below).
    private _applyCursorMove(prevDepartment: number | null, prevEmployee: number | null) {
        const leaving = this._findRosterRow(prevDepartment, prevEmployee);
        const entering = this._findRosterRow(this._activeDepartment, this._activeEmployee);

        // Recording the old value in each row's changedProperties drives its own shouldUpdate:
        // the leaving row clears its highlight, the entering one paints it.
        for (const row of new Set([leaving, entering])) {
            if (!row) {
                continue;
            }
            row.activeDate = this._activeDate;
            row.activeDepartment = this._activeDepartment;
            row.activeEmployee = this._activeEmployee;
            row.activeEntry = this._activeEntry;
        }

        const panel = this._employeeDayForm;
        if (panel) {
            panel.activeDate = this._activeDate!;
            panel.activeEmployee = this._activeEmployee;
            panel.activeDepartment = this._activeDepartment!;
            panel.activeEntry = this._activeEntry;
            panel.activeField = this._activeField;
        }

        this._cursorMoveHandled = true;
        const target = entering ?? leaving;
        void target?.updateComplete.then(() => this._scrollActiveCellIntoView(entering));
    }

    private _scrollRaf = 0;

    // "instant" overrides .main's CSS smooth scroll-behavior; "auto" would defer to it.
    private static readonly CURSOR_SCROLL_BEHAVIOR = "instant" as ScrollBehavior;

    private _scrollActiveCellIntoView(activeRow?: RosterRow) {
        // Coalesce into one pre-paint rAF so a fast WASD burst doesn't stack forced reflows.
        cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = requestAnimationFrame(() => {
            // Enable scroll-padding once navigation starts (off during load so the jump-in
            // highlight centers truly — see .cursor-nav CSS). Idempotent.
            this._main?.classList.add("cursor-nav");
            this._updateFooterScrollPadding();
            // Scope to the active row: an off-screen row keeps a stale `.active` class (its
            // shouldUpdate skips off-screen re-renders), so a document-wide query could scroll to it.
            const row = activeRow ?? this._findRosterRow(this._activeDepartment, this._activeEmployee);
            const day = row?.querySelector(".employee-day.active");
            const behavior = Roster.CURSOR_SCROLL_BEHAVIOR;
            if (day) {
                day.scrollIntoView({ behavior, block: "nearest", inline: "nearest" });
                return;
            }
            // No painted cell: the entering row is off-screen. Scroll its reserved-height host into
            // view; the IntersectionObserver then renders it. Long-jump / top↔bottom wrap case.
            row?.scrollIntoView({ behavior, block: "nearest", inline: "nearest" });
            // Column virtualization: the active day may be windowed out horizontally. Scroll its
            // column into view so the scroll listener recomputes the window and mounts the cell
            // (which then renders with `.active` — its row already holds the active props).
            const win = this._colWindow;
            if (win && this._dayColWidthPx && this._activeDate && this._main && this._data) {
                const idx = this._data.dates.indexOf(this._activeDate);
                if (idx >= 0 && (idx < win.start || idx >= win.end)) {
                    this._main.scrollLeft = Math.max(0, idx * this._dayColWidthPx - this._dayColWidthPx);
                }
            }
        });
    }

    // Footer bars (costs + the active department's targets/unassigned) stack and the unassigned
    // height varies per department, so size scroll-padding-bottom per active department rather than
    // a global worst case. Cached department; reset on every real render (updated()) to recompute.
    private _footerPaddingDept: number | null | undefined = undefined;

    private _updateFooterScrollPadding() {
        // Footer height only changes on department change, so skip the 3 getBoundingClientRect
        // reads (forced reflow) on every WASD step within the same department.
        if (this._activeDepartment === this._footerPaddingDept) {
            return;
        }
        this._footerPaddingDept = this._activeDepartment;
        const height = (el: Element | null | undefined) => el?.getBoundingClientRect().height ?? 0;
        const department =
            this._activeDepartment != null
                ? this.renderRoot.querySelector(`.department[data-department="${this._activeDepartment}"]`)
                : null;
        const footer =
            height(this.renderRoot.querySelector(".row.costs")) +
            height(department?.querySelector(".department-targets")) +
            height(department?.querySelector(".unassigned-row"));
        this.style.setProperty("--roster-scroll-padding-bottom", `${footer}px`);
    }

    async updated(changes: Map<string, unknown>) {
        this._entryHeight = app.settings.rosterDisplayMode === "regular" ? 3.5 : 2.1;
        // A real render may relayout the footer, so recompute scroll-padding on the next cursor scroll.
        this._footerPaddingDept = undefined;

        if (!this.active) {
            return;
        }

        if (changes.has("_activeTabIndex")) {
            const clampedTabIndex = Math.min(app.rosterTabs.length - 1, Math.max(0, this._activeTabIndex));
            if (clampedTabIndex !== this._activeTabIndex) {
                this._activeTabIndex = clampedTabIndex;
                this.go(null, { ...this.router.params, tab: clampedTabIndex });
            }
        }

        if (
            (changes.has("dateFrom") ||
                changes.has("dateTo") ||
                changes.has("active") ||
                changes.has("_activeTabIndex")) &&
            this.dateRange &&
            this.active
        ) {
            // The data swap is async; capture the current scroll progress now, while the old range
            // is still laid out, so _restoreScrollProgress can re-apply it once the new data renders.
            if (changes.has("dateFrom") || changes.has("dateTo")) {
                this._captureScrollProgress();
            }
            void this.synchronize(true);
        }

        // Handle roster template from URL parameter
        if (changes.has("_rosterTemplateId") && this._rosterTemplateId && this._venue) {
            const template = this._venue.rosterTemplates.find((t) => {
                return t.id === this._rosterTemplateId;
            });
            if (template) {
                this._selectRosterTemplate(template);
                // Clear the URL parameter after applying to avoid re-applying on every update
                this.go(null, { ...this.router.params, rosterTemplate: undefined });
            }
        }

        if (changes.has("_timeEntries") || changes.has("_prevTimeEntries")) {
            this._updateFrequentTimes();
        }

        if (!this._resizeObserver && this._main) {
            this._resizeObserver = new ResizeObserver(debounce(() => this._resized(), 200));
            this._resizeObserver.observe(this._main);
        }

        // _resized() reads layout geometry (offsetWidth/offsetHeight) and rewrites every
        // department header's style — a forced reflow on every update, costly on large ranges.
        // Cursor moves and drag start/end only toggle highlight/disabled state, which can't
        // affect header geometry, so skip it then. Any size/content/data change still runs it.
        if (!changesLimitedTo(changes, LAYOUT_NEUTRAL_PROPS)) {
            this._resized();
        }
        this._observeRows();
        this._observeDepartments();
        // Re-apply the pre-change scroll progress before the window recompute, so _syncColumnWindow
        // mounts the day columns for the restored offset rather than the post-clamp position.
        if (changes.has("_data")) {
            this._restoreScrollProgress();
        }
        this._syncColumnWindow();
        await wait(20);

        if (changes.has("_dragData") && this._dragData?.shiftTemplate?.positionId) {
            const availableRows = [
                ...this.renderRoot.querySelectorAll("ptc-roster-row:not([disabled])"),
            ] as RosterRow[];
            if (availableRows.length && !availableRows.some((row) => row.isVisible)) {
                this._main.scrollTop = availableRows[0].offsetTop - 200;
            }
        }
    }

    private _resized() {
        this._updateDepartmentHeaderStyles();
    }

    pageFocused() {
        if (this.active) {
            if (this._displayAutoAssignMenu) {
                this._autoAssignMenu.close();
            }
            void this.synchronize(true);
        }
    }

    private async _loadRosterNotes(showSpinner = false) {
        if (!this.dateRange) {
            return;
        }

        if (showSpinner) {
            this._loading = true;
        }

        try {
            this._rosterNotes = await app.api.getRosterNotes(new GetRosterNotesParams(this.dateRange));
        } catch (e) {
            console.error(e);
        }

        if (showSpinner) {
            this._loading = false;
        }
    }

    private _loadWeatherPredictions() {
        if (!this._venue?.city || !this.dateRange || !app.settings.rosterDisplayWeather) {
            this._weatherPredictions = Promise.resolve([]);
            return;
        }
        this._weatherPredictions = app.api.getWeatherPredictions(
            new GetWeatherPredictionsParams({ ...this.dateRange, venueId: this._venue.id })
        );
    }

    async synchronize(showSpinner = false) {
        if (!this._venue || !this.dateRange) {
            return;
        }

        this._setActive({ employee: null, department: null, date: null, entry: null });

        if (showSpinner) {
            this._loading = true;
        }

        try {
            await app.syncTimeEntries();

            const { from, to } = this.dateRange;
            const currMonth = getRange(from, "month");
            const nextMonth = getRange(to, "month");
            const twoWeeksPrior = dateAdd(this.dateRange.from, { days: -14 });
            const entriesFrom = twoWeeksPrior < currMonth.from ? twoWeeksPrior : currMonth.from;

            const worksInDepartment = (e: Employee, departmentId: number) =>
                e.positions.some((p) => p.departmentId === departmentId);

            const venueEmployeeIds = app.employees
                .filter((e) =>
                    this._venue?.departments
                        .filter((department) => app.hasAccess({ department }))
                        .some((d) => worksInDepartment(e, d.id))
                )
                .map((e) => e.id);

            const [targetsPromise, absencesPromise, schoolHolidaysPromise, availabilitesPromise, dailyResultsPromise] =
                [
                    app.api.getRosterTargets(
                        new GetRosterTargetsParams({
                            from,
                            to,
                            venue: this._venue.id,
                        })
                    ),
                    app.api.getAbsences(
                        new GetAbsencesParams({
                            from,
                            to,
                            employee: venueEmployeeIds,
                        })
                    ),
                    app.api.getSchoolHolidays(
                        new GetSchoolHolidaysParams({
                            from,
                            to,
                            country: app.company.country,
                            states: [this._venue.state],
                        })
                    ),
                    app.api.getAvailabilities(
                        new GetAvailabilitiesParams({
                            from,
                            to,
                            employees: venueEmployeeIds,
                        })
                    ),
                    app.api.getRosterDailyResults(
                        new GetDailyResultsParams({
                            from,
                            to: dateAdd(to, { days: 1 }),
                            filters: venueEmployeeIds.map((id) => ({ type: "employeeId", value: id })),
                            planned: true,
                        })
                    ),
                    this._loadRosterNotes(),
                ];

            void this._loadWeatherPredictions();

            const dailyResults = await dailyResultsPromise;

            // Discovery fetch: find historical employees who have work entries at this venue
            // but are no longer assigned here (e.g. withdrawn positions)
            const discoveryEmployeeIds = await app.getRosterVenueEmployeeIds({
                from: dateAdd(entriesFrom, { days: -14 }),
                to: nextMonth.to,
                type: [TimeEntryType.Work],
                venue: this._venue.id,
            });

            const venueEmployeeIdSet = new Set(venueEmployeeIds);
            const historicalEmployeeIds = discoveryEmployeeIds.filter((id) => !venueEmployeeIdSet.has(id));
            const allEmployeeIds = [...venueEmployeeIds, ...historicalEmployeeIds];

            // Main fetch: use employee-scoped fetch so multi-venue employees get all their entries
            const allTypes = [
                TimeEntryType.Work,
                TimeEntryType.Vacation,
                TimeEntryType.Sick,
                TimeEntryType.Free,
                TimeEntryType.CompDay,
                TimeEntryType.ChildSick,
                TimeEntryType.SickInKUG,
                TimeEntryType.HourAdjustment,
            ];

            const entriesPromise = app.getRosterTimeEntries({
                from: entriesFrom,
                to: nextMonth.to,
                type: allTypes,
                includeDeleted: true,
                includeUnassigned: true,
                employee: allEmployeeIds,
            });

            void app
                .getRosterTimeEntries({
                    from: dateAdd(entriesFrom, { days: -14 }),
                    to: entriesFrom,
                    type: allTypes,
                    includeDeleted: true,
                    includeUnassigned: true,
                    employee: allEmployeeIds,
                })
                .then((previousEntries) => (this._previousTimeEntries = previousEntries));

            const [targets, absences, schoolHolidays, availabilites, entries] = await Promise.all([
                targetsPromise,
                absencesPromise,
                schoolHolidaysPromise,
                availabilitesPromise,
                entriesPromise,
            ]);

            this._timeEntries = entries;
            this._targets = targets;
            this._absences = absences;
            this._schoolHolidays = schoolHolidays;
            this._availabilities = availabilites;
            this._dailyResults = dailyResults;
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this.refresh();

        if (showSpinner) {
            this._loading = false;

            if (this._highlightedEmployeeId !== this._activeEmployeeId) {
                queueMicrotask(() => {
                    if (!this._activeEmployeeId) {
                        return;
                    }
                    const employeeId = this._activeEmployeeId;
                    const departmentId = this._activeDepartmentId;
                    // Strip the jump-in params so a reload doesn't re-trigger the highlight;
                    // `replace` keeps Back on the origin. Capture the ids first — go() clears them.
                    this.go(null, { ...this.router.params, employee: undefined, department: undefined }, true);
                    void this._highlightEmployee(employeeId, departmentId);
                });
            }
        }
    }

    refresh() {
        if (!this._venue || !this.dateRange) {
            return;
        }

        const { from, to } = this.dateRange;
        const today = toDateString(new Date());

        const data: RosterData = {
            dates: [],
            departments: [],
            holidays: new Map<string, Holiday | null>(),
        };

        let date = from;
        while (date < to) {
            data.dates.push(date);
            const holiday = getHolidayForDate(date, {
                holidays: this._venue.enabledHolidays,
                country: app.company!.country,
                state: this._venue.state,
            });
            if (holiday) {
                data.holidays.set(date, holiday);
            }
            date = dateAdd(date, { days: 1 });
        }

        const time = this._activeTab?.time;
        const types = this._activeTab?.types;
        this._previewTimeEntries =
            (this._previewRosterTemplate &&
                this._getTimeEntriesForTemplate(this._previewRosterTemplate, this.dateRange)) ||
            [];

        this._employees = app.employees
            .map((employee) => {
                const contract = employee.contracts.find((c) => c.start <= to && (!c.end || c.end > from));
                if (!contract) {
                    return null;
                }

                const entries = this._timeEntries.filter(
                    (a) => a.employeeId === employee.id && a.date >= from && a.date < to && !a.deleted
                );

                const entriesFromToday = [...this._timeEntries, ...this._previousTimeEntries].filter(
                    (a) => a.employeeId === employee.id && a.date >= today && a.date < to && !a.deleted
                );

                const timeBalance = calculateTimeBalance(
                    app.company,
                    employee,
                    entries,
                    this._dailyResults,
                    from,
                    to,
                    today,
                    entriesFromToday
                );

                const empData: EmployeeData = {
                    employee,
                    contract,
                    timeBalance,
                };

                return empData;
            })
            .filter((e) => !!e) as EmployeeData[];

        for (const department of this._departments) {
            const depData: DepartmentData = {
                department,
                employees: [],
                unassigned: data.dates.map((date) => {
                    return {
                        date,
                        entries: [...this._timeEntries, ...(this._previewTimeEntries || [])]
                            .filter(
                                (e) =>
                                    e.date === date &&
                                    e.position?.departmentId === department.id &&
                                    !e.employeeId &&
                                    !e.deleted &&
                                    (!time || e.matchesTimeFilter(time)) &&
                                    (e.type === TimeEntryType.Work || !types || types.includes(e.type))
                            )
                            .sort(
                                (a, b) =>
                                    (a.startPlanned ? Number(a.start) : Infinity) -
                                    (b.startPlanned ? Number(b.start) : Infinity)
                            ),
                    };
                }),
            };

            const activeEmployees = app.getEmployeesForDepartment(department);
            const activeEmployeeIds = new Set(activeEmployees.map((e) => e.id));

            // Find historical employees: have time entries in this department/range but no active position
            const historicalEmployeeIds = new Set<number>();
            for (const entry of this._timeEntries) {
                if (
                    entry.position?.departmentId === department.id &&
                    entry.date >= from &&
                    entry.date < to &&
                    !entry.deleted &&
                    entry.employeeId &&
                    !activeEmployeeIds.has(entry.employeeId)
                ) {
                    historicalEmployeeIds.add(entry.employeeId);
                }
            }
            const historicalEmployees = [...historicalEmployeeIds]
                .map((id) => this._employees.find((e) => e.employee.id === id))
                .filter((e): e is EmployeeData => !!e && !e.employee.deleted)
                .map((e) => e.employee);

            const allEmployees: { employee: Employee; historical: boolean }[] = [
                ...activeEmployees.map((e) => ({ employee: e, historical: false })),
                ...historicalEmployees.map((e) => ({ employee: e, historical: true })),
            ];

            for (const { employee, historical } of allEmployees) {
                const commitBefore = app.company?.settings.commitTimeEntriesBefore;
                const empData = this._employees.find((e) => e.employee.id === employee.id);
                if (!empData) {
                    continue;
                }
                const entries = [...this._timeEntries, ...(this._previewTimeEntries || [])].filter(
                    (a) => a.employeeId === employee.id && a.date >= from && a.date < to
                );
                const days: DayData[] = [];

                for (const date of data.dates) {
                    const holiday = data.holidays.get(date) || null;
                    const contract = employee.getContractForDate(date);
                    const todaysEntries = entries
                        .filter(
                            (a) =>
                                a.date === date &&
                                !a.deleted &&
                                (app.settings.rosterMirrorDepartments ||
                                    !a.position ||
                                    a.position.departmentId === department.id) &&
                                (!time || a.matchesTimeFilter(time)) &&
                                a.type !== TimeEntryType.HourAdjustment &&
                                (a.type === TimeEntryType.Work || !types || types.includes(a.type))
                        )
                        .sort(
                            (a, b) =>
                                (a.startPlanned ? Number(a.start) : Infinity) -
                                (b.startPlanned ? Number(b.start) : Infinity)
                        );
                    const absence = this._absences.find(
                        (a) =>
                            a.employeeId === employee.id &&
                            a.start <= date &&
                            a.end > date &&
                            (a.status === AbsenceStatus.Approved || a.status === AbsenceStatus.Inferred)
                    );
                    days.push({
                        employee: employee.id,
                        date,
                        blocked: !contract || contract.blocked,
                        blockedReason: (contract && contract.comment) || "Inaktiv",
                        today: date === today,
                        entries: todaysEntries,
                        readonly:
                            historical ||
                            isDayReadonly(date, {
                                today,
                                commitBefore,
                                canEditPast: app.hasPermission("manage.employees.time"),
                            }),
                        absence,
                        availabilities: this._availabilities
                            .filter((a) => a.employeeId === employee.id && a.date === date)
                            .sort((a, b) => ((a.start || "") < (b.start || "") ? -1 : 1)),
                        holiday,
                    });
                }

                depData.employees.push({
                    employeeData: empData,
                    days,
                    historical,
                });
            }

            data.departments.push(depData);
        }

        this._data = data;
        this._issues = app.employees
            .flatMap((e) =>
                getShiftIssues(e, app.company.settings, app.company, this._timeEntries, {
                    from,
                    to: dateAdd(to, { days: 1 }),
                })
            )
            .filter((issue) => !issue.ignored);
        this._employeeDayForm && this._employeeDayForm.updateForms();

        this._rosterCosts && this._rosterCosts.updateConfig();
        for (const t of this._rosterTargetsElements) {
            t.requestUpdate();
        }

        this._loadDayStats();
    }

    private _loadDayStats = debounce(() => this._employeeDayForm?.loadStats(), 500);

    stateChanged() {
        if (this.active) {
            this.debouncedRefresh();
        }
    }

    debouncedRefresh = debounce(() => this.refresh(), 100);

    private _updateFilter() {
        this._filterString = this._filterInput.value;
    }

    private _availablePositions(employee: Employee, dep: Department) {
        return employee.positions
            .filter((position) => position.active && position.departmentId === dep.id)
            .sort((a, b) => a.order - b.order);
    }

    private async _addTimeEntry(vals: Partial<TimeEntry>, setActive = false) {
        if (!vals.employeeId && vals.type !== TimeEntryType.Work) {
            return;
        }

        if (![TimeEntryType.Work, TimeEntryType.CompDay, TimeEntryType.Free].includes(vals.type!)) {
            if (!app.hasPermission("manage.employees.absences")) {
                void alert("Sie haben keine ausreichenden Berechtigungen für diese Aktion!", {
                    type: "warning",
                    title: "Fehlende Berechtigung",
                });
                return;
            }

            const absence = new Absence({
                type: vals.type,
                employeeId: vals.employeeId!,
                start: vals.date,
                end: dateAdd(vals.date!, { days: 1 }),
            });

            return this._editAbsence(absence);
        }

        const entry = new TimeEntry(vals);
        this._trackBalanceRefetch(entry);
        await app.createOrUpdateTimeEntries(entry, { applyAutoMeals: true });
        this._timeEntries.push(entry);
        this.refresh();

        if (entry.type === TimeEntryType.CompDay) {
            // to have a correct time balance, we need the .result to be calculated before updating the view
            await this.synchronize(true);
        }

        if (setActive) {
            this._setActive({
                employee: entry.employeeId,
                date: entry.date,
                department: entry.position && entry.position.departmentId,
                entry: entry.id,
                field: "start",
            });
        }
        return;
    }

    private async _removeTimeEntry(entry: TimeEntry) {
        if (entry.isPast && (entry.startFinal || entry.endFinal)) {
            const employee = entry.employeeId && app.getEmployee(entry.employeeId);
            if (!app.hasPermission("manage.employees.time") || (employee && !app.hasPermissionForEmployee(employee))) {
                void alert("Sie haben keine Berechtigung für diese Aktion!", { type: "warning" });
                return;
            }
            if (
                !(await confirm("Sind Sie sicher dass Sie diesen Eintrag löschen möchten?", "Löschen", "Abbrechen", {
                    title: "Eintrag Löschen",
                    type: "destructive",
                    icon: "icon.action.delete",
                    optionsLayout: "horizontal",
                }))
            ) {
                return;
            }
        }

        if (entry.id === this._activeEntry) {
            const activeDep = this._data.departments.find((d) => d.department.id === this._activeDepartment);
            const activeEmp =
                activeDep && activeDep.employees.find((e) => e.employeeData.employee.id === this._activeEmployee);
            const days = activeEmp?.days || activeDep?.unassigned;
            const activeDay = days?.find((d) => d.date === this._activeDate);
            const index = activeDay && activeDay.entries.findIndex((e) => e.id === this._activeEntry);
            const entry =
                (index && activeDay!.entries[index - 1]) || activeDay!.entries.find((e) => e.id !== this._activeEntry);
            this._setActive({ entry: (entry && entry.id) || "new" });
        }
        this._trackBalanceRefetch(entry);
        void app.removeTimeEntries(entry);
        if (!entry.published) {
            this._timeEntries = this._timeEntries.filter((e) => e.id !== entry.id);
        }
        this.refresh();
    }

    private _updateFrequentTimes() {
        this._frequentTimes = calculateFrequentTimes(this._timeEntries, this._previousTimeEntries, this._activeTab);

        this.requestUpdate("_frequentTimes");
    }

    private _pendingBalanceEmployeeIds = new Set<number>();
    private _balanceRefetchTimeout?: ReturnType<typeof setTimeout>;

    private _timeEntriesSyncedHandler = () => {
        if (!this._pendingBalanceEmployeeIds.size) {
            return;
        }
        clearTimeout(this._balanceRefetchTimeout);
        this._balanceRefetchTimeout = setTimeout(() => void this._refetchDailyResults(), 500);
    };

    private _trackBalanceRefetch(...entries: (TimeEntry | null)[]) {
        for (const entry of entries) {
            if (entry?.employeeId) {
                this._pendingBalanceEmployeeIds.add(entry.employeeId);
            }
        }
    }

    private async _refetchDailyResults() {
        const employeeIds = [...this._pendingBalanceEmployeeIds].filter((id) => !app.hasPendingTimeEntryWrites(id));
        for (const id of employeeIds) {
            this._pendingBalanceEmployeeIds.delete(id);
        }
        if (!employeeIds.length || !this.dateRange) {
            return;
        }
        try {
            const results = await app.api.getRosterDailyResults(
                new GetDailyResultsParams({
                    from: this.dateRange.from,
                    to: dateAdd(this.dateRange.to, { days: 1 }),
                    filters: employeeIds.map((id) => ({ type: "employeeId", value: id })),
                    planned: true,
                })
            );
            const refetched = new Set(employeeIds);
            this._dailyResults = [
                ...this._dailyResults.filter((r) => !r.employeeId || !refetched.has(r.employeeId)),
                ...results,
            ];
            this.refresh();
        } catch (e) {
            console.error(e);
        }
    }

    private _updateTimeEntry(a: TimeEntry, data: Partial<TimeEntry> = {}) {
        if (app.isRemoved(a)) {
            return;
        }

        this._trackBalanceRefetch(a);
        const { employeeId, ...rest } = data;
        Object.assign(a, rest);
        if (employeeId !== undefined) {
            assignEmployee(a, employeeId);
        }
        this._trackBalanceRefetch(a);

        void app.createOrUpdateTimeEntries(a);
        this.refresh();
    }

    private _toggleDepartment({ id }: { id: number }, collapsed: boolean = !this._collapsed.has(id)) {
        if (collapsed) {
            this._collapsed.add(id);
            this._collapsedSuggestions.add(id);
        } else {
            this._collapsed.delete(id);
        }
        this.requestUpdate();
    }

    private _toggleDepartmentSuggestions(
        { id }: { id: number },
        collapsed: boolean = !this._collapsedSuggestions.has(id)
    ) {
        if (collapsed) {
            this._collapsedSuggestions.add(id);
        } else {
            this._collapsedSuggestions.delete(id);
            this._collapsed.delete(id);
        }
        this.requestUpdate();
    }

    private dropIntoDay(e: DragEvent, employee: Employee | null, department: Department, date: DateString) {
        e.preventDefault();

        const data = this._dragData;

        if (!data) {
            return;
        }

        let id: string | undefined = undefined;
        let type: TimeEntryType = TimeEntryType.Work;
        let position: Position | null = null;
        let startPlanned: Date | null = null;
        let endPlanned: Date | null = null;
        let startFinal: Date | null = null;
        let endFinal: Date | null = null;
        let breakPlanned: Hours | null = null;

        if (data.entry) {
            id = data.entry.id;
            type = data.entry.type || TimeEntryType.Work;
            position = data.entry.position || null;
            ({ startPlanned, endPlanned, startFinal, endFinal } = moveTimesToDate(data.entry, date));
            breakPlanned = data.entry.breakPlanned || null;
        } else {
            [startPlanned, endPlanned] = parseTimes(date, data.shiftTemplate?.start, data.shiftTemplate?.end);
            position =
                (data.shiftTemplate?.positionId && app.getPosition(data.shiftTemplate?.positionId)?.position) || null;
            type = data.shiftTemplate?.type || TimeEntryType.Work;

            breakPlanned = data.shiftTemplate?.breakPlanned ? (data.shiftTemplate.breakPlanned as Hours) : null;
        }

        const availablePositions = employee ? this._availablePositions(employee, department) : department.positions;

        if (type === TimeEntryType.Work) {
            if (!position || !availablePositions.some((p) => p.id === position!.id)) {
                position = availablePositions[0];
            }
        } else {
            position = null;
        }

        if (!id || e.altKey || this._isCopyDrag) {
            const times = applyPastDateFlip(date, {
                startPlanned,
                endPlanned,
                startFinal,
                endFinal,
                employeeId: employee?.id ?? null,
            });
            void this._addTimeEntry(
                {
                    type,
                    ...times,
                    position,
                    date,
                    breakPlanned,
                },
                this._activeEntry === id || (type === TimeEntryType.Work && !times.startPlanned)
            );
        } else {
            if (data.fromReadonlyDay) {
                (e.target as HTMLElement).classList.remove("dragover");
                return;
            }
            const entry = this._timeEntries.find((e) => e.id === id);
            if (!entry) {
                return;
            }
            entryPositions.set(`${department.id}_${entry.id}`, {
                x: e.pageX - (data.imageOffset?.x || 0),
                y: e.pageY - (data.imageOffset?.y || 0),
            });

            this._updateTimeEntry(entry, {
                position,
                date,
                employeeId: employee?.id || null,
                startPlanned,
                endPlanned,
                startFinal,
                endFinal,
            });
            if (this._activeEntry === entry.id) {
                this._setActive({
                    date,
                    department: position && position.departmentId,
                    employee: employee?.id || null,
                });
            }
        }

        (e.target as HTMLElement).classList.remove("dragover");

        setTimeout(() => this._dragend(), 50);
    }

    private _beginDrag(data: DragData) {
        this._dragData = data;
        this._isCopyDrag = false;
    }

    private _dragstart(e: DragEvent, data: DragData) {
        this._beginDrag(data);
        const el = e.target as HTMLElement;
        const imgEl = (el.shadowRoot && (el.shadowRoot.querySelector(".container") as HTMLElement)) || el;
        const dt = e.dataTransfer!;
        dt.setData("text/plain", "42");
        dt.effectAllowed = "all";
        dt.dropEffect = "move";
        data.imageOffset = { x: imgEl.offsetWidth / 2, y: imgEl.offsetHeight / 2 };
        dt.setDragImage(imgEl, data.imageOffset.x, data.imageOffset.y);
        this.classList.add("dragging");
        el.classList.add("dragging");
    }

    private _dragenter(e: DragEvent) {
        e.preventDefault();
        (e.target as HTMLElement).classList.add("dragover");
    }

    private _dragover(e: DragEvent) {
        if (!isSafari) {
            this._isCopyDrag = e.altKey;
        }
    }

    private _dragleave(e: DragEvent) {
        (e.target as HTMLElement).classList.remove("dragover");
    }

    private _dragend(e?: DragEvent) {
        this._dragData = null;
        this._isCopyDrag = false;
        this.classList.remove("dragging");
        for (const el of [this, ...this.renderRoot!.querySelectorAll(".dragging")] as HTMLElement[]) {
            el.classList.remove("dragging");
        }
        e && e.preventDefault();
    }

    private _toggleFavorite(template: ShiftTemplate) {
        const shiftTemplates = [...app.shiftTemplates];
        const existing = shiftTemplates.findIndex(
            (t) =>
                t.venue === template.venue &&
                t.start === template.start &&
                t.end === template.end &&
                t.positionId === template.positionId
        );
        if (existing === -1) {
            template.favorite = true;
            shiftTemplates.push(template);
        } else {
            template.favorite = false;
            shiftTemplates.splice(existing, 1);
        }
        app.shiftTemplates = shiftTemplates;
        this.requestUpdate();
    }

    private async _publishChanges({ detail: { timeEntries } }: CustomEvent<{ timeEntries: TimeEntry[] }>) {
        if (timeEntries.length) {
            const res = await this._publishRosterDialog.show({
                venue: this._venue!,
                entries: this._timeEntries,
                unpublishedEntries: timeEntries,
                absences: this._absences,
                ...this.dateRange!,
            });
            if (res) {
                await this.synchronize(true);
            }
        }
    }

    private async _print(url: string) {
        return print(url);
    }

    private async _shareViaMessage(url: string) {
        const venue = this._venue!;
        const departments = this._filteredDepartments;
        const from = formatDate(this.dateRange!.from);
        const to = formatDate(dateAdd(this.dateRange!.to, { days: -1 }));
        const entireVenue = !departments || departments.length === venue.departments.length;

        await this._sendMessageDialog.show({
            venues: entireVenue ? [venue] : undefined,
            departments: entireVenue ? undefined : departments,
            message: `Hallo zusammen,

Es ist ein neuer Dienstplan für den Zeitraum vom ${from} bis ${to} verfügbar!
Diesen kannst du über folgenden Link einsehen:

${url}

Liebe Grüße,
${app.profile!.name}`,
        });
    }

    private async _createRosterTemplate() {
        this._rosterTemplatesPopover.hide();
        const result = await this._createRosterTemplateDialog.show({
            timeEntries: this._timeEntries,
            departments: this._filteredDepartments,
        });
        if (!result) {
            return;
        }

        this._loading = true;
        const { error } = await this.createTemplateFromCurrentView(result.name, result.departmentIds);
        this._loading = false;

        if (error) {
            void alert(
                error === "no_shifts"
                    ? "Es wurden keine Schichten gewählt! (Dienstplanvorlagen umfassen nur Schichten mit Planzeiten)"
                    : error,
                { type: "warning" }
            );
            return;
        }

        void alert("Dienstplanvorlage erfolgreich erstellt.", { title: "Vorlage Erstellt", type: "success" });
    }

    private async _deleteRosterTemplate(template: RosterTemplate) {
        const confirmed = await confirm(
            `Wollen Sie die Vorlage "${template.name}" entfernen?`,
            "Entfernen",
            "Abbrechen",
            {
                type: "destructive",
                title: "Vorlage Entfernen",
                optionsLayout: "horizontal",
                icon: "icon.action.delete",
            }
        );
        if (!confirmed) {
            return;
        }

        this._loading = true;
        try {
            await app.updateVenue(
                new UpdateVenueParams({
                    id: this._venue!.id,
                    rosterTemplates: this._venue!.rosterTemplates.filter((t) => t.id !== template.id),
                })
            );
            this.requestUpdate();
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private _selectRosterTemplate(template: RosterTemplate | null) {
        if (this._rosterTemplatesPopover) {
            this._rosterTemplatesPopover.hide();
        }
        this._previewRosterTemplate = template;
        this.refresh();
    }

    private async _applyRosterTemplate() {
        if (!this._previewRosterTemplate || !this._previewTimeEntries) {
            return;
        }

        const entries = this._previewTimeEntries;
        entries.forEach((entry) => delete entry.preview);

        this._loading = true;
        try {
            await app.createOrUpdateTimeEntries(entries, { applyAutoMeals: true });
            void alert(`Es wurden ${entries.length} Schichten erfolgreich eingefügt.`, {
                type: "success",
                title: "Vorlage Eingefügt",
            });
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;

        this._selectRosterTemplate(null);

        await this.synchronize(true);
    }

    async whenReady(timeout = 10000): Promise<boolean> {
        const interval = 200;
        let waited = 0;
        while (this._loading && waited < timeout) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, interval));
            waited += interval;
        }
        return !this._loading;
    }

    async applyPreviewedTemplate(): Promise<{ success: boolean; count?: number; error?: string }> {
        if (!this._previewRosterTemplate || !this._previewTimeEntries) {
            return { success: false, error: "no_template_previewed" };
        }

        const entries = this._previewTimeEntries;
        entries.forEach((entry) => delete entry.preview);

        this._loading = true;
        try {
            await app.createOrUpdateTimeEntries(entries, { applyAutoMeals: true });
        } catch (e) {
            this._loading = false;
            return { success: false, error: e.message };
        }
        this._loading = false;

        this._selectRosterTemplate(null);
        await this.synchronize(true);
        return { success: true, count: entries.length };
    }

    isRosterTemplatePreviewActive(): boolean {
        return Boolean(this._previewRosterTemplate);
    }

    discardPreviewedTemplate(): { success: boolean; error?: string } {
        if (!this._previewRosterTemplate) {
            return { success: false, error: "no_template_previewed" };
        }
        this._selectRosterTemplate(null);
        return { success: true };
    }

    async createTemplateFromCurrentView(
        name: string,
        departmentIds?: number[]
    ): Promise<{ success: boolean; error?: string }> {
        if (!this._venue) {
            return { success: false, error: "no_venue" };
        }

        if (!this.dateRange) {
            return { success: false, error: "no_date_range" };
        }

        const time = this._activeTab?.time;
        const { from, to } = this.dateRange;
        const departmentSet = departmentIds && new Set(departmentIds);

        const isPlannedShift = (e: TimeEntry) =>
            e.date >= from &&
            e.date < to &&
            e.type === TimeEntryType.Work &&
            !e.deleted &&
            !app.isRemoved(e) &&
            e.planned &&
            e.position?.active;

        const matchesScope = (e: TimeEntry) =>
            (!time || e.matchesTimeFilter(time)) &&
            (!departmentSet || (e.position && departmentSet.has(e.position.departmentId)));

        const shifts = this._timeEntries
            .filter((e) => isPlannedShift(e) && matchesScope(e))
            .filter((e): e is TimeEntry & { position: Position; startPlanned: Date; endPlanned: Date } =>
                Boolean(e.position && e.startPlanned && e.endPlanned)
            )
            .map((e) => ({
                day: e.start.getDay(),
                employee: e.employeeId,
                position: e.position.id,
                start: e.startPlanned.toTimeString().slice(0, 5),
                end: e.endPlanned.toTimeString().slice(0, 5),
                break: e.breakPlanned,
            }));

        if (!shifts.length) {
            return { success: false, error: "no_shifts" };
        }

        const template = new RosterTemplate();
        template.name = name;
        template.shifts = shifts;

        try {
            await app.updateVenue(
                new UpdateVenueParams({
                    id: this._venue.id,
                    rosterTemplates: [...this._venue.rosterTemplates, template],
                })
            );
            this.requestUpdate();
            return { success: true };
        } catch (e) {
            return { success: false, error: e.message };
        }
    }

    async showAutoAssign(weights?: Partial<AutoAssignCriteria>): Promise<{ success: boolean; error?: string }> {
        if (!this.dateRange) {
            return { success: false, error: "no_date_range" };
        }
        if (inferRangeType(this.dateRange) !== "week") {
            return { success: false, error: "not_week_view" };
        }
        if (!this._enabledFeatures.rosterAutoAssign) {
            return { success: false, error: "feature_disabled" };
        }

        await this._showAutoAssign();

        if (weights) {
            await this._autoAssignMenu.setWeights(weights);
        }

        return { success: true };
    }

    isAutoAssignActive(): boolean {
        return this._displayAutoAssignMenu;
    }

    async applyAutoAssign(): Promise<{ success: boolean; error?: string }> {
        if (!this._displayAutoAssignMenu || !this._autoAssignMenu) {
            return { success: false, error: "auto_assign_not_active" };
        }
        try {
            await this._autoAssignMenu.apply();
            return { success: true };
        } catch (e) {
            return { success: false, error: (e as Error).message };
        }
    }

    discardAutoAssign(): { success: boolean; error?: string } {
        if (!this._displayAutoAssignMenu || !this._autoAssignMenu) {
            return { success: false, error: "auto_assign_not_active" };
        }
        this._autoAssignMenu.close();
        return { success: true };
    }

    async unassignAllShifts(): Promise<{ success: boolean; count?: number; error?: string }> {
        const range = this.dateRange;
        if (!range) {
            return { success: false, error: "no_date_range" };
        }

        try {
            const today = toDateString(new Date());
            const entries = this._timeEntries.filter(
                (e) =>
                    !e.deleted &&
                    e.type === TimeEntryType.Work &&
                    e.date > today &&
                    e.date >= range.from &&
                    e.date < range.to
            );

            entries.forEach((e) => assignEmployee(e, null));

            void app.updateSettings({ rosterDisplayUnassigned: true });
            await app.createOrUpdateTimeEntries(entries, { applyAutoMeals: true });
            await this.synchronize(true);
            return { success: true, count: entries.length };
        } catch (e) {
            return { success: false, error: (e as Error).message };
        }
    }

    private async _unassignAll() {
        if (!this.dateRange) return;

        const confirmed = await confirm(
            'Wollen Sie wirklich alle Schichten in "Nicht zugewiesen" verschieben? Nur zukünftige Schichten werden verschoben.',
            "Zuweisungen aufheben",
            "Abbrechen",
            {
                type: "destructive",
                title: "Schichtzuweisungen aufheben",
                optionsLayout: "horizontal",
                icon: "icon.status.unassigned",
            }
        );
        if (confirmed) {
            await this.unassignAllShifts();
        }
    }

    private _getTimeEntriesForTemplate(template: RosterTemplate, { from, to }: DateRange) {
        const entries: TimeEntry[] = [];

        let date = from;

        while (date < to) {
            const weekDay = new Date(date).getDay();
            const shifts = template.shifts.filter((t) => t.day === weekDay);

            for (const shift of shifts) {
                const { position: positionId, start, end, break: breakPlanned } = shift;
                let employeeId = shift.employee;

                const { position } = app.getPosition(positionId) || { position: null };
                const employee = employeeId ? app.getEmployee(employeeId) : null;
                const contract = employee?.getContractForDate(date);
                const absence = this._absences.find(
                    (a) =>
                        a.employeeId === employeeId &&
                        a.start <= date &&
                        a.end > date &&
                        (a.status === AbsenceStatus.Approved || a.status === AbsenceStatus.Inferred)
                );

                if (!position || !position.active) {
                    continue;
                }

                if (
                    !employee ||
                    !!absence ||
                    !contract ||
                    contract.blocked ||
                    !employee.positions.some((p) => p.id === position.id)
                ) {
                    employeeId = null;
                }

                const entry = new TimeEntry({
                    employeeId,
                    position,
                    positionId: position.id,
                    date,
                    preview: true,
                });

                const [startTS, endTS] = parseTimes(date, start, end);
                entry.startPlanned = startTS;
                entry.endPlanned = endTS;
                entry.breakPlanned = breakPlanned ?? null;
                applyPastDateFlip(entry.date, entry);

                const time = this._activeTab?.time;
                const departments = this._activeTab?.departments;

                if (
                    app.hasAccess({ timeEntry: entry }) &&
                    (!departments || departments.includes(entry.position!.departmentId)) &&
                    (!time || entry.matchesTimeFilter(time)) &&
                    // Deduplicate entries in case template is applied twice
                    (!entry.employeeId ||
                        !this._timeEntries.some(
                            (e) =>
                                !e.deleted &&
                                e.employeeId === entry.employeeId &&
                                e.positionId === entry.positionId &&
                                e.start.getTime() === entry.start.getTime() &&
                                e.end.getTime() === entry.end.getTime()
                        ))
                ) {
                    entries.push(entry);
                }
            }
            date = dateAdd(date, { days: 1 });
        }

        return entries;
    }

    private async _clearTimeEntries(e: Event) {
        this._clearRosterDialog.hide();
        e.preventDefault();

        if (!this.dateRange) {
            return;
        }

        const selected = new FormData(e.target as HTMLFormElement);

        const entries = filterClearableEntries(
            this._timeEntries,
            app.employees,
            this._filteredDepartments.map((d) => d.id),
            this._activeTab,
            this.dateRange,
            (entry) => app.hasAccess({ timeEntry: entry }),
            {
                includePlanned: selected.has("planned"),
                includeFinished: selected.has("finished"),
            }
        );

        const confirmed = await confirm(
            `Sind Sie sicher, dass Sie diese ${entries.length} Einträge löschen ` +
                `möchten? Diese Aktion kann nicht rückgängig gemacht werden!`,
            `${entries.length} Einträge Löschen`,
            "Abbrechen",
            { title: "Dienstplan Leeren", type: "destructive" }
        );
        if (!confirmed) {
            return;
        }

        // TODO: removeTimeEntries will resolve immediately, since we did not set the wait flag
        // however both these functions seem to do some sort of synchronization, so maybe refactor this
        await app.removeTimeEntries(entries);
        await this.synchronize(true);
    }

    private _getTemplateDepartments(t: RosterTemplate) {
        const departments = new Map<number, { name: string; color: string; count: number; id: number }>();
        for (const shift of t.shifts) {
            const r = app.getPosition(shift.position);
            if (!r) {
                continue;
            }

            if (!departments.has(r.department.id)) {
                departments.set(r.department.id, {
                    id: r.department.id,
                    name: r.department.name,
                    color: r.department.color,
                    count: 0,
                });
            }

            departments.get(r.department.id)!.count++;
        }
        return [...departments.values()];
    }

    private async _goToIssue(issue: Issue) {
        this._issuePopover.hide();

        const shift = getShiftsForIssue(issue).find((s) => Boolean(s.positionId));

        if (shift?.positionId) {
            const department = app.getPosition(shift.positionId)?.department;

            if (!department) {
                return;
            }

            this._setActive({
                date: shift.date,
                employee: shift.employeeId,
                department: department.id,
                entry: shift.id,
            });
        }
    }

    private _updateRosterOrder = debounce((id: number, rosterOrder: string[]) => {
        void app.updateDepartment({ id, rosterOrder });
    }, 3000);

    private _moveEmployee(dep: DepartmentData, i: number, direction: "up" | "down") {
        const employees = dep.employees;
        const employee = employees[i];
        employees.splice(i, 1);
        employees.splice(direction === "up" ? i - 1 : i + 1, 0, employee);
        const rosterOrder = employees.map((e) => e.employeeData.employee.id.toString());
        // Reorder mutates dep.employees in place without swapping _data, so the nav cache (keyed
        // on data identity + signature) wouldn't see it — drop it so WASD walks the new order.
        this._navigation.invalidate();
        this.requestUpdate();
        dep.department.rosterOrder = rosterOrder;
        this._updateRosterOrder(dep.department.id, rosterOrder);
    }

    private _setActiveTimeout: number;
    private _setActive(
        {
            date = this._activeDate,
            employee = this._activeEmployee,
            department = this._activeDepartment,
            entry = this._activeEntry,
            field = this._activeField,
        }: {
            date?: DateString | null;
            employee?: number | null;
            department?: number | null;
            entry?: string | null;
            field?: string | null;
        } = {},
        instant = false
    ) {
        if (date && employee && department && !entry) {
            const first = this._timeEntries.find(
                (e) =>
                    e.employeeId === employee &&
                    e.date === date &&
                    (!e.position || e.position.departmentId === department) &&
                    !e.deleted &&
                    (!this._activeTab?.time || e.matchesTimeFilter(this._activeTab.time))
            );
            entry = (first && first.id) || "new";
        }

        const innerAsyncUpdate = async () => {
            this._activeDate = date;
            this._activeEmployee = employee;
            this._activeDepartment = department || this._activeDepartment;
            this._activeEntry = entry;
            this._activeField = field;
            await this.updateComplete;
            // The cursor-move fast path already scheduled the scroll; don't scroll again here.
            if (this._cursorMoveHandled) {
                this._cursorMoveHandled = false;
                return;
            }
            this._scrollActiveCellIntoView();
        };

        clearTimeout(this._setActiveTimeout);

        if (entry || instant) {
            void innerAsyncUpdate();
        } else {
            this._setActiveTimeout = window.setTimeout(innerAsyncUpdate, 500);
        }
    }

    private _dayInput() {
        const entryEls =
            (this._activeEntry &&
                (this.renderRoot!.querySelectorAll(
                    `ptc-roster-entry[id^="entry-${this._activeEntry}"]`
                ) as unknown as RosterEntry[])) ||
            [];
        for (const el of entryEls) {
            el.requestUpdate();
        }
    }

    private async _editAbsence(absence: Absence) {
        const changes = await this._absenceDialog.show(absence);
        if (changes) {
            await this.synchronize(true);
        }
    }

    private async _newAvailability(employeeId: number, date: DateString) {
        const created = await this._availabilityDialog.show(
            new Availability({
                employeeId,
                status: AvailabilityStatus.Available,
                date,
            })
        );
        if (created) {
            this._availabilities = await app.api.getAvailabilities(
                new GetAvailabilitiesParams({
                    ...this.dateRange,
                    employees: app.employees.map((e) => e.id),
                })
            );
            this.refresh();
        }
    }

    private async _editAvailability(av: Availability) {
        const edited = await this._availabilityDialog.show(av);
        if (edited) {
            this._availabilities = await app.api.getAvailabilities(
                new GetAvailabilitiesParams({
                    ...this.dateRange,
                    employees: app.employees.map((e) => e.id),
                })
            );
            this.refresh();
        }
    }

    private _updateDepartmentHeaderStyles() {
        const main = this.renderRoot.querySelector(".main") as HTMLDivElement;
        const rosterHeader = this.renderRoot.querySelector(".roster-header") as HTMLDivElement;

        if (!main || !rosterHeader) {
            return;
        }

        // Publish the geometry as custom properties on the host; every .department-header
        // reads them via var() (see styles). One write here instead of one per header.
        this.style.setProperty("--roster-dept-header-width", `${main.offsetWidth - 24}px`);
        this.style.setProperty("--roster-dept-header-top", `${rosterHeader.offsetHeight + 8}px`);
        // Publish the department-header height too so scroll-padding-top clears both sticky layers.
        const deptHeader = this.renderRoot.querySelector(".department-header") as HTMLDivElement | null;
        this.style.setProperty("--roster-dept-header-height", `${deptHeader?.offsetHeight ?? 0}px`);

        // Inline scroll-padding for the sticky columns: left = name column + .balance-before,
        // right = .balance-after (both collapse to 0 when time balances are hidden).
        const firstColumn = rosterHeader.querySelector(".filter-wrapper") as HTMLElement | null;
        const balanceBefore = rosterHeader.querySelector(".balance-before") as HTMLElement | null;
        const balanceAfter = rosterHeader.querySelector(".balance-after") as HTMLElement | null;
        const leftPad = (firstColumn?.offsetWidth ?? 0) + (balanceBefore?.offsetWidth ?? 0);
        this.style.setProperty("--roster-scroll-padding-left", `${leftPad}px`);
        this.style.setProperty("--roster-scroll-padding-right", `${balanceAfter?.offsetWidth ?? 0}px`);
    }

    private async _showAutoAssign() {
        if (!this.dateRange) {
            return;
        }
        this._collapsed.clear();
        this.classList.add("auto-assigning");
        this._displayAutoAssignMenu = true;
        await this.updateComplete;
        this._autoAssignMenu.init({
            entries: this._timeEntries,
            absences: this._absences,
            availabilities: this._availabilities,
            employees: this._employees,
            weights: {
                matchNominalHours: 3,
                reduceAccumulatedOvertime: 2,
                considerAvailabilities: 1,
            },
            company: app.company!,
            range: this.dateRange,
            departments: this._activeTab?.departments || this._venue?.departments.map((d) => d.id) || [],
        });
    }

    private async _closeAutoAssign() {
        this.classList.remove("auto-assigning");
        this._displayAutoAssignMenu = false;
        this.refresh();
    }

    private async _undoChanges({ detail: { timeEntries } }: CustomEvent<{ timeEntries: TimeEntry[] }>) {
        if (
            !(await confirm(
                "Möchten Sie die gewählten Änderungen rückgängig machen und den Status der letzten Veröffentlichung wiederherstellen?",
                `${timeEntries.length} Änderungen Verwerfen`,
                "Abbrechen",
                { title: "Änderungen Verwerfen", icon: "icon.action.reset", optionsLayout: "horizontal" }
            ))
        ) {
            return;
        }

        const unpublished = timeEntries.filter((e) => !e.published);
        const published = timeEntries.filter((e) => Boolean(e.published));

        for (const entry of published) {
            entry.date = entry.published!.date;
            entry.startPlanned = entry.published!.startPlanned;
            entry.endPlanned = entry.published!.endPlanned;
            entry.comment = entry.published!.comment || "";
            entry.deleted = entry.published!.deleted;
            entry.positionId = entry.published!.positionId;
            entry.position = (entry.positionId && app.getPosition(entry.positionId)?.position) || null;
            assignEmployee(entry, entry.published!.employeeId);
        }

        // the updated should resolve immediately, since we didnt set the wait flag
        void app.createOrUpdateTimeEntries(published);
        void app.removeTimeEntries(unpublished, false, true);

        // synchronize the roster in the background
        void this.synchronize(true);
    }

    private async _addRosterNote(date: DateString) {
        if (!this._activeTab) {
            return;
        }
        if (!app.settings.rosterDisplayNotes) {
            await app.updateSettings({ rosterDisplayNotes: true });
        }
        await this._rosterNotesEl.addRosterNote(
            createRosterNote({
                companyId: app.company.id,
                start: date,
                end: dateAdd(date, { days: 1 }),
                filters: this._activeTab.departments
                    ? this._activeTab.departments.map((value) => ({ type: "department", value }))
                    : [{ type: "venue", value: this._activeTab.venue }],
            })
        );
    }

    static styles = [
        shared,
        Checkbox.styles,
        RosterCosts.styles,
        RosterTargetsElement.styles,
        css`
            :host {
                display: block;
                position: relative;
                --roster-motion-scale: 1;
                --roster-scroll-behavior: smooth;
            }

            @media (prefers-reduced-motion: reduce) {
                :host {
                    --roster-motion-scale: 0;
                    --roster-scroll-behavior: auto;
                }
            }

            :host(.auto-assigning) .main {
                cursor: not-allowed;
            }

            :host(:not(.auto-assigning)) ptc-roster-entry {
                transition: none !important;
                transform: none !important;
                opacity: 1 !important;
            }

            :host(.auto-assigning) .main > * {
                pointer-events: none;
            }

            .main {
                overflow: auto;
                scroll-behavior: var(--roster-scroll-behavior);
                isolation: isolate;
            }

            /* Applied lazily (first cursor scroll) so the initial jump-in highlight still
               centres in the scroller's true middle, not the padded region. */
            .main.cursor-nav {
                scroll-padding-top: calc(var(--roster-dept-header-top, 0px) + var(--roster-dept-header-height, 0px));
                scroll-padding-left: var(--roster-scroll-padding-left, 0px);
                scroll-padding-right: var(--roster-scroll-padding-right, 0px);
                scroll-padding-bottom: var(--roster-scroll-padding-bottom, 0px);
            }

            .main-inner {
                position: relative;
            }

            .main:has(.highlight) {
                .row:not(.highlight) {
                    opacity: 0.2;
                }
            }

            ptc-roster-row {
                min-height: var(--roster-row-height);
            }

            /* On large ranges (>9 days) keep-mounted rows leave a lot of off-screen GPU raster;
               content-visibility:auto releases it without unmounting. inline "none" keeps a
               never-rendered row at 0 width so scroll geometry stays stable. Safari <18 ignores it. */
            .main-outer.large-range ptc-roster-row.employee-row {
                content-visibility: auto;
                contain-intrinsic-block-size: auto var(--roster-row-height);
                contain-intrinsic-inline-size: auto none;
            }

            /* The unassigned row must stay layout-visible for position:sticky to work. */
            .main-outer.large-range .unassigned-row ptc-roster-row {
                content-visibility: visible;
            }

            .row {
                display: flex;
                flex-direction: row;
                min-width: fit-content;
                transition: opacity calc(var(--roster-motion-scale) * 0.5s) ease-in-out;
            }

            .row > * {
                min-width: 9em;
                width: 0;
                box-sizing: border-box;
                flex: 1;
            }

            .main-outer {
                --max-roster-day-entries: 3;
                --roster-day-gap: 3px;
                --roster-entry-height: calc(var(--roster-row-height) - var(--roster-day-gap) * 2);
                --roster-day-max-height: calc(
                    (var(--roster-entry-height) + var(--roster-day-gap)) * var(--max-roster-day-entries) +
                        var(--roster-day-gap)
                );
                --roster-unassigned-row-height: 3.5em;

                overflow: visible;
            }

            .main-outer.minimal .row > * {
                min-width: 2.5em;
            }

            /* Phase 1 of column virtualization: on large ranges give day cells a definite width
               so a windowed render can bracket them with sized spacers. Excludes the sticky name
               column, the balance columns, and the full-width roster-notes and cost chart overlays. */
            .main-outer.large-range {
                --roster-day-width: 9em;
            }

            .main-outer.large-range.minimal {
                --roster-day-width: 2.5em;
            }

            .main-outer.large-range
                .row
                > *:not(:first-child):not(.balance-before):not(.balance-after):not(ptc-roster-notes):not(
                    ptc-ui-chart
                ):not(.day-spacer) {
                flex: 0 0 var(--roster-day-width);
                min-width: var(--roster-day-width);
                width: var(--roster-day-width);
            }

            .day-spacer {
                align-self: stretch;
            }

            .row > :first-child {
                width: 200px;
                flex: none;
            }

            .row:not(:first-child) {
                border-top: solid 1px var(--shade-1);
            }

            .row > :not(:last-child) {
                border-right: solid 1px var(--shade-1);
            }

            .row > .today {
                border-left: solid 1px var(--blue-bg);
            }

            .roster-header {
                position: sticky;
                top: 0;
                background: rgba(255, 255, 255, 0.95);
                z-index: 25;
                margin-bottom: 0.5em;
                border-bottom: solid 1px var(--shade-1);
                min-width: fit-content;
            }

            .filter-wrapper {
                position: sticky;
                left: 0;
                background: rgba(255, 255, 255, 0.95);
                display: flex;
                flex-direction: column;
                justify-content: center;
                z-index: 5;
            }

            .filter-wrapper ptc-ui-icon[name="icon.action.search"] {
                color: var(--shade-5);
            }

            .filter-wrapper:focus-within ptc-ui-icon[name="icon.action.search"] {
                color: var(--color-primary);
            }

            .row > .roster-notes-header,
            .row > .weather-header {
                position: sticky;
                left: 0;
                background: rgba(255, 255, 255, 0.95);
                z-index: 9;
                border-right: none;
            }

            .day-header {
                font-weight: bold;
                text-align: center;
                padding: 0.5em;
                position: relative;
            }

            .day-subheader {
                font-size: 80%;
                opacity: 0.7;
                margin-top: 2px;
            }

            .day-header.holiday {
                color: var(--violet);
            }

            .day-header.sunday {
                color: var(--orange);
            }

            .day-header.today {
                color: var(--blue);
            }

            .employee-day::before {
                content: "";
                display: block;
                position: absolute;
                inset: 0;
                background-clip: content-box;
                opacity: 0.05;
                z-index: -1;
            }

            .employee-day.sunday::before {
                background: var(--orange);
            }

            .employee-day.holiday::before {
                background: var(--violet);
            }

            .employee-day.today::before {
                background: var(--blue);
            }

            .department {
                margin-bottom: 0.5em;
                min-width: fit-content;
            }

            .department-header {
                letter-spacing: 5px;
                background: transparent;
                border-bottom: solid 2px;
                margin-bottom: 0.5em;
                color: var(--color-highlight);
                grid-column: span 8;
                text-align: center;
                font-weight: bold;
                font-size: 80%;
                cursor: pointer;
                position: relative;
                padding: 4px 0 2px 0;
                box-sizing: border-box;
                border-radius: 5px;
                border: solid 2px var(--color-highlight);
                background: rgba(255, 255, 255, 0.95);
                position: sticky;
                left: 8px;
                z-index: 23;
                width: var(--roster-dept-header-width);
                top: var(--roster-dept-header-top);
            }

            .department-body {
                display: contents;
            }

            .department-header:hover::after {
                content: "";
                display: block;
                position: absolute;
                left: 0;
                right: 0;
                top: 0;
                bottom: 0;
                background: rgba(255, 255, 255, 0.3);
            }

            .department.collapsed > .department-body {
                display: none;
            }

            .department.collapsed > .department-header {
                background: var(--color-highlight);
                color: #fff;
            }

            .employee-row:not(:last-child) {
                border-bottom: solid 1px var(--shade-1);
            }

            .employee-day {
                display: grid;
                grid-auto-rows: var(--roster-entry-height);
                max-height: var(--roster-day-max-height);
                grid-gap: var(--roster-day-gap);
                padding: var(--roster-day-gap);
                overflow: clip;
                position: relative;
                cursor: pointer;
            }

            .employee-day[disabled] ptc-roster-entry {
                pointer-events: auto;
            }

            /* Only cells that can overflow become scroll containers; a plain cell as a scroll
               container registers a non-fast-scrollable region (~1.2k of them on a large range). */
            .employee-day.scrollable {
                overflow-x: hidden;
                overflow-y: auto;
                scrollbar-width: thin;
                scrollbar-color: var(--color-highlight) color-mix(in hsl, transparent 80%, var(--color-highlight));
            }

            .employee-day > :not(:last-child):not(.add-button) {
                margin-bottom: 0;
            }

            .employee-day:not(:has(ptc-roster-entry)) {
                grid-template-rows: 1fr;
            }

            .employee-day.dragover {
                background: var(--color-primary-bg);
            }

            .employee-day.blocked {
                background: var(--shade-1);
            }

            .employee-day.active {
                background: var(--shade-1);
            }

            .employee-day .blocked-reason {
                text-align: center;
                opacity: 0.5;
            }

            .employee-header {
                cursor: pointer;
                position: relative;
                position: sticky;
                left: 0;
                z-index: 20;
                gap: 3px;
                align-items: center;
                padding: 0.2em;
                background: rgba(255, 255, 255, 0.95);
            }

            .employee-row.historical .employee-header {
                opacity: 0.75;
            }

            .employee-header:hover .employee-name {
                color: var(--color-primary);
            }

            .employee-header .stretch {
                width: 0;
            }

            .employee-header ptc-avatar {
                font-size: 0.95em;
            }

            .employee-move-buttons {
                display: flex;
                flex-direction: column;
                position: absolute;
                right: 0.3em;
                top: 0.3em;
            }

            .employee-header:not(:hover) .employee-move-buttons {
                display: none;
            }

            .employee-move-buttons button {
                padding: 0 0.1em;
                background: rgba(255, 255, 255, 0.9);
            }

            .employee-move-buttons button:first-child {
                margin-bottom: 0.1em;
            }

            .employee-name {
                font-weight: 600;
                line-height: 1.2em;
            }

            .add-button {
                display: grid;
                place-content: center;
                height: auto;
                background: rgba(255, 255, 255, 0.7) !important;
                z-index: 1;
                inset: var(--roster-day-gap);
                top: auto;
                position: absolute;
                min-height: var(--roster-entry-height);
                height: calc(
                    100% - var(--entries) * var(--roster-entry-height) - var(--entries) *
                        var(--roster-day-gap) - var(--roster-day-gap) * 2
                );

                ptc-ui-icon {
                    font-size: var(--font-size-tiny);
                    line-height: 1.2;
                }
            }

            .employee-day:has(.availabilities) .add-button {
                pointer-events: none;
            }

            .employee-day:hover .add-button {
                box-shadow: inset var(--shade-1) 0 0 0 1000px;
            }

            .employee-day:not(:hover) .add-button:not(:focus):not(.selected) {
                display: none;
            }

            ptc-roster-entry {
                z-index: 2;
                cursor: pointer !important;
            }

            .selected,
            .box.inverted {
                box-shadow: var(--color-primary) 0 0 0 0.2em !important;
            }

            [draggable="true"] {
                cursor: grab;
                user-select: none;
            }

            [draggable="true"]:active {
                cursor: grabbing;
            }

            ptc-roster-entry.dragging {
                opacity: 0.5;
            }

            ptc-roster-entry,
            .shift-template-wrapper {
                transition: all calc(var(--roster-motion-scale) * 0.1s);
            }

            ptc-roster-entry[draggable="true"]:hover,
            .shift-template-wrapper:hover {
                box-shadow: var(--color-highlight, var(--shade-4)) 0 0 0 0.2em;
            }

            :host(.dragging) {
                cursor: grabbing;
            }

            :host(.dragging) .employee-day *:not(.dragging) {
                pointer-events: none;
            }

            .row.costs {
                position: sticky;
                bottom: 0;
                z-index: 24;
                border-top: solid 1px var(--shade-2);
                background: rgba(255, 255, 255, 0.95);
            }

            .absence {
                --gap: 3px;
                --absence-middle-width: calc(100% + var(--gap) * 2);
                --absence-edge-width: calc(100% + var(--gap));
                --absence-standalone-width: 100%;

                --half-day-background: color-mix(in oklch, var(--color-highlight), var(--color-bg));

                z-index: 2;
                position: relative;
                color: var(--color-bg);

                &:only-child {
                    border-radius: 0.5em;
                }

                ptc-ui-icon {
                    z-index: 1;
                }

                &::before {
                    content: "";
                    display: block;
                    background: var(--color-highlight);
                    position: absolute;
                    inset: 0 calc(var(--gap) * -1);
                    width: var(--absence-middle-width);
                    height: 100%;
                    border-radius: inherit;
                }

                &:hover::before {
                    box-shadow: inset var(--shade-1) 0 0 0 1000px;
                }

                &.absence-start {
                    &::before {
                        width: var(--absence-edge-width);
                        inset-inline-start: 0;
                    }
                }

                &.absence-end {
                    &::before {
                        width: var(--absence-edge-width);
                        inset-inline-end: 0;
                    }
                }

                &.absence-start.absence-end {
                    &::before {
                        width: var(--absence-standalone-width);
                        inset-inline: 0;
                    }
                }
            }

            .absence-start {
                border-top-left-radius: 0.5em;
                border-bottom-left-radius: 0.5em;
            }

            .absence-end {
                border-top-right-radius: 0.5em;
                border-bottom-right-radius: 0.5em;
            }

            .absence.half-day {
                position: relative;

                &::before {
                    background: var(--half-day-background);
                }

                &::after {
                    z-index: 0;
                    content: "";
                    display: block;
                    inset: 0 calc(var(--gap) * -1);
                    position: absolute;
                    background: var(--color-highlight);
                    width: var(--absence-middle-width);
                    clip-path: var(--path-diagonal-half);
                    border-radius: inherit;
                }

                &.absence-start {
                    &::after {
                        width: var(--absence-edge-width);
                        inset-inline-start: 0;
                    }
                }

                &.absence-end {
                    &::after {
                        width: var(--absence-edge-width);
                        inset-inline-end: 0;
                    }
                }

                &.absence-start.absence-end {
                    &::after {
                        width: var(--absence-standalone-width);
                        inset-inline-start: 0;
                        inset-inline-end: 0;
                    }
                }
            }

            .unassigned-row {
                position: sticky;
                bottom: 0;
                z-index: 23;
                border-top: solid 1px var(--shade-1);
                border-bottom: solid 1px var(--shade-1);
                color: var(--color-highlight);
                background: var(--color-bg);
                transition: height calc(var(--roster-motion-scale) * 0.3s);

                ptc-roster-row {
                    min-height: var(--roster-unassigned-row-height);
                }
            }

            .show-charts .department-targets {
                bottom: 4.3em;
            }

            .show-charts:not(.show-targets) .unassigned-row {
                bottom: 4.3em;
            }

            .show-charts.show-targets .unassigned-row {
                bottom: 7em;
            }

            .show-targets:not(.show-charts) .unassigned-row {
                bottom: 2.75em;
            }

            /* Each per-department sticky-bottom row is a full-width composited layer; with many
               departments on a large range that dominates GPU memory. Drop the stickiness for the
               targets row there (the global costs row and the header stay sticky), but keep the
               unassigned row pinned — it's the one users drag open shifts from (PTC-3816). */
            .main-outer.large-range .department-targets {
                position: static;
            }

            .main-outer.compact .employee-header ptc-avatar,
            .main-outer.minimal .employee-header ptc-avatar {
                font-size: 0.6em;
            }

            .main-outer.compact .employee-header ptc-ui-progress,
            .main-outer.compact .employee-header .employee-status,
            .main-outer.compact .employee-header .employee-move-buttons,
            .main-outer.minimal .employee-header ptc-ui-progress,
            .main-outer.minimal .employee-header .employee-status,
            .main-outer.minimal .employee-header .employee-move-buttons {
                display: none;
            }

            .main-outer.compact .absence ptc-ui-icon,
            .main-outer.minimal .absence ptc-ui-icon {
                font-size: var(--font-size-medium);
            }

            .add-roster-note {
                position: absolute;
                margin: auto;
                background: var(--color-bg) !important;
                bottom: 0.2em;
                left: 0.2em;
                width: calc(100% - 0.4em);
                display: block;
                opacity: 1 !important;
                padding-top: 0.2em !important;
                padding-bottom: 0.2em !important;
            }

            .day-header:not(:hover) .add-roster-note {
                display: none;
            }

            .row > .balance-before,
            .row > .balance-after {
                min-width: 5em;
                width: 5em;
                flex: none;
                font-size: var(--font-size-smaller);
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0.1em;
                font-weight: 600;
                background: rgba(255, 255, 255, 0.95);
                position: sticky;
                z-index: 20;
            }

            .balance-before {
                left: 200px;
            }

            .balance-after {
                right: 0;
                border-left: solid 1px var(--shade-1);
            }

            .shift-template {
                padding: 0.5em 0.5em 0.5em 0.7em;
                background: var(--color-bg);
                letter-spacing: 0.05em;
            }

            .shift-template-wrapper {
                border-radius: 0.5em;
            }

            .shift-template.favorite .star {
                color: var(--yellow);
                font-weight: bold;
            }

            .shift-template .star {
                cursor: pointer;
            }

            .shift-template:not(.favorite) .star:not(:hover),
            .shift-template.favorite .star:hover {
                opacity: 0.5;
            }

            .availabilities {
                z-index: 0;
                display: grid;
                grid-auto-rows: 1fr;
            }

            .availability {
                margin: 0;
                background: color-mix(in hsl, var(--color-highlight) 20%, transparent);
                width: 100%;
                text-align: center;
                padding: 0.2em;
                box-sizing: border-box;

                & > * {
                    width: 100%;
                }
            }

            .availability-icon {
                background: var(--color-highlight);
                color: var(--color-bg);
            }

            .availability-icon.cutoff-topleft {
                clip-path: polygon(100% 0, 100% 100%, 0 100%);
            }

            .availability-icon.cutoff-bottomright {
                clip-path: polygon(100% 0, 0 0, 0 100%);
            }

            @media print {
                .main-outer,
                .main,
                .main-inner {
                    position: static !important;
                    display: block !important;
                    width: auto !important;
                    height: auto !important;
                    max-width: none;
                }

                .main-outer > * {
                    height: auto !important;
                }

                .department-header {
                    width: 100% !important;
                }

                .employee-row {
                    page-break-inside: avoid;
                }
            }
        `,
    ];

    private _renderUnassigned(dep: DepartmentData) {
        if (!app.settings.rosterDisplayUnassigned || app.settings.rosterDisplayMode === "minimal") {
            return;
        }

        // Stacking is shared with the navigation grid so WASD walks the same entries.
        const today = toDateString(new Date());
        const commitBefore = app.company?.settings.commitTimeEntriesBefore;
        const canEditPast = app.hasPermission("manage.employees.time");
        const days: DayData[] = dep.unassigned.map(({ date, entries }) => {
            const stackedEntries = groupUnassignedStacks(entries).map((stack) => {
                const entry = stack[0];
                // Add stack size as a property that can be accessed in rendering
                (entry as any).stackSize = stack.length;
                return entry;
            });

            return {
                employee: undefined, // No employee for unassigned
                date,
                entries: stackedEntries,
                blocked: false,
                blockedReason: "",
                today: date === today,
                readonly: isDayReadonly(date, { today, commitBefore, canEditPast }),
                availabilities: [],
                holiday: this._data.holidays.get(date) || null,
            };
        });

        // Key on the column count for the same reason as the employee rows (see _renderDepartment):
        // the unassigned row is a reused content-visibility row and would otherwise keep its old,
        // wider remembered width after the range shrinks, pinning the horizontal scroll width.
        return keyed(
            `unassigned-${dep.department.id}-${this._data.dates.length}`,
            html`
                <div class="unassigned-row">
                    <ptc-roster-row
                        .draggingFromReadonlyDay=${!!this._dragData?.fromReadonlyDay}
                        .days=${days}
                        .department=${dep}
                        .colWindow=${this._colWindow}
                        .colWidth=${this._dayColWidthPx}
                        .venue=${this._venue!}
                        .activeDate=${this._activeDate}
                        .activeDepartment=${this._activeDepartment}
                        .activeEmployee=${this._activeEmployee}
                        .activeEntry=${this._activeEntry}
                        .activeTab=${this._activeTab}
                        .issues=${this._issues}
                        .canMoveUp=${false}
                        .canMoveDown=${false}
                        @select=${({
                            detail: { date, entry, department, field, instant },
                        }: CustomEvent<{
                            date?: DateString;
                            entry?: string | null;
                            department: number | null;
                            field?: string | null;
                            instant?: boolean;
                        }>) =>
                            this._setActive(
                                {
                                    date,
                                    department,
                                    entry,
                                    field,
                                    employee: null,
                                },
                                instant
                            )}
                        @begindrag=${(e: CustomEvent<{ data: DragData }>) => this._beginDrag(e.detail.data)}
                        @remove=${({ detail: { entry } }: CustomEvent<{ entry: TimeEntry }>) =>
                            this._removeTimeEntry(entry)}
                        @drop-into-day=${({
                            detail: { dragEvent, department, employee, date },
                        }: CustomEvent<{
                            dragEvent: DragEvent;
                            department: Department;
                            employee: Employee | null;
                            date: DateString;
                        }>) => this.dropIntoDay(dragEvent, employee, department, date)}
                        class="row employee-row"
                    ></ptc-roster-row>
                </div>
            `
        );
    }

    private _renderDepartment(dep: DepartmentData) {
        if (
            !this._activeTab ||
            (this._activeTab.departments && !this._activeTab.departments.includes(dep.department.id))
        ) {
            return;
        }

        if (!this.dateRange) {
            return;
        }

        const dateRange = this.dateRange;
        const employees = dep.employees
            .filter(
                ({
                    employeeData: {
                        employee: { name, staffNumber },
                    },
                }) => `${name} ${staffNumber}`.toLowerCase().includes(this._filterString.toLowerCase())
            )
            .filter((e) => !!e.employeeData.employee.getContractForRange(dateRange));

        const collapsed = !employees.length || (!this._filterString && this._collapsed.has(dep.department.id));
        const dragPositionId = this._dragData?.entry?.positionId || this._dragData?.shiftTemplate?.positionId;
        const dragPosition = dragPositionId && app.getPosition(dragPositionId)?.position;
        return html`
            <div
                class="department ${collapsed ? "collapsed" : ""}"
                style="--color-highlight: ${colors[dep.department.color] || dep.department.color}"
                ?disabled=${dragPosition && dragPosition.departmentId !== dep.department.id}
                data-department=${dep.department.id}
            >
                <div class="department-header" @click=${() => this._toggleDepartment(dep.department)}>
                    ${dep.department.name.toUpperCase()}
                    ${collapsed
                        ? html`<ptc-ui-icon name="icon.toggle.expand"></ptc-ui-icon>`
                        : html`<ptc-ui-icon name="icon.toggle.expand-active"></ptc-ui-icon>`}
                </div>

                <!-- spacer row to make sure parent element retains it's width when collapsed -->
                <div class="row" style="height: 0" ?hidden=${!collapsed}>
                    <div></div>
                    <div class="balance-before" ?hidden=${!app.settings.rosterDisplayTimeBalances}></div>
                    ${renderDayWindow(
                        this._data.dates,
                        this._colWindow,
                        this._dayColWidthPx,
                        () => html`<div class="employee-day"></div>`
                    )}
                    <div class="balance-after" ?hidden=${!app.settings.rosterDisplayTimeBalances}></div>
                </div>

                <div class="department-body ${this._previewRosterTemplate ? "non-interactive" : ""}">
                    ${employees.map((emp, i) => {
                        // Key on the column count: a content-visibility:auto row remembers its last
                        // rendered width, and Chrome keeps that remembered size across an in-app
                        // range change (the element is reused), so a shrunk range would keep
                        // stretching scroll width to the old, wider size. Re-keying on the column
                        // count recreates the row fresh when the range length changes, dropping the
                        // stale remembered width.
                        return keyed(
                            `${emp.employeeData.employee.id}-${this._data.dates.length}`,
                            html`
                                <ptc-roster-row
                                    .draggingFromReadonlyDay=${!!this._dragData?.fromReadonlyDay}
                                    id="emp-${emp.employeeData.employee.id}"
                                    .employee=${emp.employeeData}
                                    .days=${emp.days}
                                    .department=${dep}
                                    .releaseOffscreen=${this._data.dates.length > 9}
                                    .colWindow=${this._colWindow}
                                    .colWidth=${this._dayColWidthPx}
                                    .venue=${this._venue!}
                                    .activeDate=${this._activeDate}
                                    .activeDepartment=${this._activeDepartment}
                                    .activeEmployee=${this._activeEmployee}
                                    .activeEntry=${this._activeEntry}
                                    .activeTab=${this._activeTab}
                                    .issues=${this._issues}
                                    .canMoveUp=${!emp.historical && i > 0}
                                    .canMoveDown=${!emp.historical && i < dep.employees.length - 1}
                                    @move-up=${() => this._moveEmployee(dep, i, "up")}
                                    @move-down=${() => this._moveEmployee(dep, i, "down")}
                                    @header-clicked=${() => this.go(`employees/${emp.employeeData.employee.id}/time`)}
                                    @select=${({
                                        detail: { date, entry, department, field, instant },
                                    }: CustomEvent<{
                                        date?: DateString;
                                        entry?: string | null;
                                        department: number | null;
                                        field?: string | null;
                                        instant?: boolean;
                                    }>) =>
                                        this._setActive(
                                            {
                                                date,
                                                department,
                                                entry,
                                                field,
                                                employee: emp.employeeData.employee.id,
                                            },
                                            instant
                                        )}
                                    @begindrag=${(e: CustomEvent<{ data: DragData }>) => this._beginDrag(e.detail.data)}
                                    @remove=${({ detail: { entry } }: CustomEvent<{ entry: TimeEntry }>) =>
                                        this._removeTimeEntry(entry)}
                                    @drop-into-day=${({
                                        detail: { dragEvent, department, employee, date },
                                    }: CustomEvent<{
                                        dragEvent: DragEvent;
                                        department: Department;
                                        employee: Employee;
                                        date: DateString;
                                    }>) => this.dropIntoDay(dragEvent, employee, department, date)}
                                    @edit-absence=${(e: CustomEvent<{ absence: Absence }>) =>
                                        this._editAbsence(e.detail.absence)}
                                    class="row employee-row"
                                    ?disabled=${emp.historical ||
                                    (dragPosition &&
                                        !emp.employeeData.employee.positions.some((p) => p.id === dragPosition!.id))}
                                ></ptc-roster-row>
                            `
                        );
                    })}
                    ${this._renderUnassigned(dep)} ${this._renderTargets(dep.department)}
                </div>
            </div>
        `;
    }

    private _renderTargets(department: Department) {
        if (
            !app.settings.rosterDisplayTargets ||
            app.settings.rosterDisplayMode === "minimal" ||
            !this.dateRange ||
            this._previewRosterTemplate
        ) {
            return;
        }

        const { from, to } = this.dateRange;

        return html`
            <ptc-roster-targets
                .targets=${this._targets}
                .entries=${this._timeEntries}
                .department=${department}
                .editable=${app.hasPermission(`manage.planning.roster`)}
                .from=${from}
                .to=${to}
                .colWindow=${this._colWindow}
                .colWidth=${this._dayColWidthPx}
                class="noprint"
            ></ptc-roster-targets>
        `;
    }

    private _renderCosts() {
        const range = this.dateRange;
        if (
            !range ||
            !this._venue ||
            !app.hasPermission(`manage.roster.costs`) ||
            !app.settings.rosterDisplayCosts ||
            this._previewRosterTemplate
        ) {
            return;
        }
        return html`
            <ptc-roster-costs
                .venue=${this._venue}
                .entries=${this._timeEntries}
                .from=${range.from}
                .to=${range.to}
                .colWindow=${this._colWindow}
                .colWidth=${this._dayColWidthPx}
                class="noprint"
                .departments=${this._filteredDepartments}
            ></ptc-roster-costs>
        `;
    }

    private _renderDayMenu() {
        if (this._previewRosterTemplate) {
            return;
        }
        return html`
            <div class="noprint relative" style="width: 18em; border-left: solid 1px var(--shade-1);">
                <ptc-employee-day
                    class="fullbleed"
                    .entries=${[...this._timeEntries, ...this._previousTimeEntries]}
                    .absences=${this._absences}
                    .availabilities=${this._availabilities}
                    .activeDate=${this._activeDate!}
                    .activeEmployee=${this._activeEmployee!}
                    .activeDepartment=${this._activeDepartment!}
                    .activeEntry=${this._activeEntry}
                    .activeField=${this._activeField}
                    .issues=${this._issues}
                    .timeFilter=${this._activeTab?.time}
                    .active=${!!this.active && !!this._activeDate && !!this._activeDepartment}
                    @select=${({
                        detail: { date, entry, department, field, instant },
                    }: CustomEvent<{
                        date?: DateString;
                        entry?: string | null;
                        department: number | null;
                        field?: string | null;
                        instant?: boolean;
                    }>) =>
                        this._setActive(
                            {
                                date,
                                department,
                                entry,
                                field,
                            },
                            instant
                        )}
                    @addentry=${(e: CustomEvent<Partial<TimeEntry>>) => this._addTimeEntry(e.detail, true)}
                    @updateentry=${(e: CustomEvent<TimeEntry>) => this._updateTimeEntry(e.detail)}
                    @input=${() => this._dayInput()}
                    @close=${() => this._setActive({ date: null, department: null, entry: null, field: null }, true)}
                    @remove=${(e: CustomEvent<{ entry: TimeEntry }>) => this._removeTimeEntry(e.detail.entry)}
                    @begindrag=${(e: CustomEvent<{ data: DragData }>) => this._beginDrag(e.detail.data)}
                    @edit-availability=${(e: CustomEvent<{ availability: Availability }>) =>
                        this._editAvailability(e.detail.availability)}
                    @new-availability=${() => this._newAvailability(this._activeEmployee!, this._activeDate!)}
                ></ptc-employee-day>
            </div>
        `;
    }

    private _renderAutoAssignMenu() {
        if (this._previewRosterTemplate || !this.dateRange) {
            return;
        }
        return html` <div class="noprint relative" style="width: 18em; border-left: solid 1px var(--shade-1);">
            <ptc-auto-assign-menu
                class="fullbleed"
                @close=${this._closeAutoAssign}
                @updated=${() => this.refresh()}
                .disabled=${inferRangeType(this.dateRange) !== "week"}
            ></ptc-auto-assign-menu>
        </div>`;
    }

    private _renderDayHeader() {
        const today = toDateString(new Date());
        if (!this.dateRange || !this._venue) {
            return;
        }
        return html`
            <div class="roster-header">
                <div class="row">
                    <div class="filter-wrapper half-spacing center-aligning horizontal layout">
                        <ptc-ui-icon name="icon.action.search" class="left-margined"></ptc-ui-icon>
                        <input
                            class="plain stretch"
                            id="filterInput"
                            type="text"
                            placeholder="Suchen..."
                            @input=${this._updateFilter}
                            @keydown=${(e: Event) => e.stopPropagation()}
                        />
                    </div>

                    <div class="balance-before" ?hidden=${!app.settings.rosterDisplayTimeBalances}>
                        <div><ptc-ui-icon name="icon.time-entry.adjustment"></ptc-ui-icon></div>
                    </div>

                    ${renderDayWindow(this._data.dates, this._colWindow, this._dayColWidthPx, (date) => {
                        const holiday = this._data.holidays.get(date);
                        return html`
                            <div
                                class=${classMap({
                                    "day-header": true,
                                    holiday: !!holiday,
                                    today: date === today,
                                    sunday: new Date(date).getDay() === 0,
                                })}
                                data-date=${date}
                            >
                                ${app.settings.rosterDisplayMode === "minimal"
                                    ? html`<div class="smaller negatively-margined">
                                          <div>${formatWeekDayShort(date)}</div>
                                          <div class="smaller day-subheader">${formatDateShort(date, true)}</div>
                                      </div>`
                                    : html`
                                          ${formatWeekDay(date)}
                                          <div class="day-subheader ellipsis">
                                              ${holiday ? holiday.name : formatDate(date)}
                                          </div>
                                          <button
                                              class="skinny top-margined smaller subtle add-roster-note"
                                              ?hidden=${!app.hasPermission(`manage.roster.notes`)}
                                              @click=${() => this._addRosterNote(date)}
                                          >
                                              <ptc-ui-icon name="icon.object.note"></ptc-ui-icon>
                                              Notiz
                                          </button>
                                      `}
                            </div>
                        `;
                    })}

                    <div class="balance-after" ?hidden=${!app.settings.rosterDisplayTimeBalances}>
                        <div>
                            <div><ptc-ui-icon name="icon.time-entry.adjustment"></ptc-ui-icon></div>
                        </div>
                    </div>
                </div>

                <div class="row" ?hidden=${!app.settings.rosterDisplayNotes}>
                    <div class="half-padded semibold roster-notes-header">
                        <ptc-ui-icon name="icon.object.date"></ptc-ui-icon>
                        Kalendereinträge
                    </div>
                    <div class="balance-before" ?hidden=${!app.settings.rosterDisplayTimeBalances}></div>
                    <ptc-roster-notes
                        .rosterNotes=${this._filteredRosterNotes}
                        .schoolHolidays=${this._schoolHolidays}
                        .holidays=${getHolidaysInRange(this.dateRange.from, this.dateRange.to, {
                            holidays: this._venue.enabledHolidays,
                            country: app.company.country,
                            state: this._venue.state,
                        })}
                        .dateRange=${this.dateRange}
                        @roster-notes-updated=${() => this._loadRosterNotes(true)}
                    ></ptc-roster-notes>
                    <div class="balance-after" ?hidden=${!app.settings.rosterDisplayTimeBalances}></div>
                </div>

                <div class="row" ?hidden=${!app.settings.rosterDisplayWeather}>
                    <div class="half-padded semibold weather-header">
                        <ptc-ui-icon name="icon.object.weather"></ptc-ui-icon>
                        Wetter
                    </div>
                    <div class="balance-before" ?hidden=${!app.settings.rosterDisplayTimeBalances}></div>

                    ${renderDayWindow(this._data.dates, this._colWindow, this._dayColWidthPx, (date) => {
                        return html` <div class="relative">
                            ${until(
                                this._weatherPredictions.then((predictions) => {
                                    const prediction = predictions.find((prediction) => prediction.date === date);
                                    return html`
                                        <ptc-weather
                                            class="fill"
                                            .data=${prediction}
                                            .city=${this._venue?.city}
                                            .minimal=${app.settings.rosterDisplayMode === "minimal"}
                                        ></ptc-weather>
                                    `;
                                }),
                                html`
                                    <div class="centering layout fill">
                                        <ptc-ui-spinner style="font-size: 0.4em" active></ptc-ui-spinner>
                                    </div>
                                `
                            )}
                        </div>`;
                    })}
                    <div class="balance-after" ?hidden=${!app.settings.rosterDisplayTimeBalances}></div>
                </div>
            </div>
        `;
    }

    private _renderRosterTemplatePreview() {
        if (!this._previewRosterTemplate) {
            return;
        }
        const template = this._previewRosterTemplate;

        return html`<div class="padded spacing end-aligning horizontal layout border-bottom">
            <div class="padded stretch">
                <div class="smaller blue colored-text bottom-margined">
                    <ptc-ui-icon name="icon.action.apply-template"></ptc-ui-icon>
                    Vorlage Anwenden
                </div>
                <div class="bottom-margined semibold ellipsis">${template.name}</div>
                <div class="smaller horizontal spacing wrapping layout">
                    ${this._filteredDepartments.map((dep) => {
                        const count = this._previewTimeEntries?.filter((e) =>
                            dep.positions.some((pos) => pos.id === e.positionId)
                        ).length;
                        if (!count) {
                            return;
                        }
                        return html`<div
                            class="box"
                            style="padding: 0.25em 0.5em; --color-highlight: ${colors[dep.color] || dep.color}"
                        >
                            ${dep.name}
                            <span class="bold">${count}</span>
                        </div>`;
                    })}
                </div>
            </div>
            <div class="smaller horizontal spacing layout">
                <button class="primary" @click=${this._applyRosterTemplate} style="width: 15em;">Anwenden</button>
                <button class="ghost" @click=${() => this._selectRosterTemplate(null)} style="width: 15em;">
                    Abbrechen
                </button>
            </div>
        </div>`;
    }

    private _renderGlobalShiftSuggestions() {
        if (!app.settings.rosterDisplaySuggestions) {
            return;
        }
        const favOnly = app.settings.rosterDisplayFavSuggestionsOnly;

        const templates = this._frequentTimes.filter(
            ({ venue, favorite }) => venue === this._venue!.id && (!favOnly || favorite)
        );
        const templatesByDepartment = this._filteredDepartments.map((department) => ({
            department,
            templates: templates.filter((t) => department.positions.some((p) => p.id === t.positionId)),
        }));

        const absences: ShiftTemplate[] = [{ type: TimeEntryType.CompDay }, { type: TimeEntryType.Free }];

        if (app.hasPermission("manage.employees.absences")) {
            absences.unshift({ type: TimeEntryType.Sick }, { type: TimeEntryType.Vacation });
        }

        return html`
            <div class="noprint vertical layout" style="width: 18em; border-left: solid 1px var(--shade-1);">
                <div class="small half-padded horizontal center-aligning half-spacing layout border-bottom">
                    <div class="padded stretch semibold">
                        <ptc-ui-icon name="icon.toggle.suggestions"></ptc-ui-icon> Vorschläge & Favoriten
                    </div>

                    <button
                        class="small skinny transparent ${app.settings.rosterDisplayFavSuggestionsOnly
                            ? "yellow bold"
                            : ""}"
                        title="Nur Favoriten Anzeigen"
                        @click=${() =>
                            app.updateSettings({
                                rosterDisplayFavSuggestionsOnly: !app.settings.rosterDisplayFavSuggestionsOnly,
                            })}
                    >
                        <ptc-ui-icon name="icon.toggle.favorite"></ptc-ui-icon>
                    </button>

                    <button
                        class="skinny transparent text-left-aligning"
                        @click=${() => app.updateSettings({ rosterDisplaySuggestions: false })}
                        style="margin-right: 0.25em"
                    >
                        <div class="horizontal spacing layout">
                            <ptc-ui-icon name="icon.action.close"></ptc-ui-icon>
                        </div>
                    </button>
                </div>

                <div class="padded spacing evenly stretching horizontal layout">
                    ${absences.map(
                        (t) => html`
                            <div
                                class="shift-template-wrapper"
                                style="--color-highlight: ${timeEntryTypeColor(t.type!)}"
                                title="${app.localized.timeEntryTypeLabel({ type: t.type! })}"
                            >
                                <div
                                    class="shift-template text-centering padded box"
                                    draggable="true"
                                    @dragstart=${(e: DragEvent) =>
                                        this._dragstart(e, {
                                            shiftTemplate: t,
                                        })}
                                >
                                    <ptc-ui-icon name="${app.localized.timeEntryTypeIcon(t.type!)}"></ptc-ui-icon>
                                </div>
                                <ptc-popover trigger="hover" class="non-interactive tooltip" alignment="bottom">
                                    <div class="larger">${app.localized.timeEntryTypeLabel({ type: t.type! })}</div>
                                    <div class="subtle smaller">
                                        <ptc-ui-icon name="icon.action.drag"></ptc-ui-icon> Per Drag & Drop Einfügen
                                    </div>
                                </ptc-popover>
                            </div>
                        `
                    )}
                </div>

                <ptc-scroller class="stretch collapse" stable-gutter>
                    ${templatesByDepartment.map(
                        ({ department, templates }) => html`
                            <div class="vertical layout">
                                <button
                                    class="slim transparent horizontally-margined"
                                    style="--color-highlight: ${colors[department.color] || department.color}"
                                    @click=${() => this._toggleDepartmentSuggestions(department)}
                                >
                                    <div class="horizontal center-aligning layout">
                                        <div class="stretch ellipsis text-left-aligning collapse">
                                            ${department.name}
                                        </div>
                                        ${this._collapsedSuggestions.has(department.id)
                                            ? html`<ptc-ui-icon name="icon.toggle.expand"></ptc-ui-icon>`
                                            : html`<ptc-ui-icon name="icon.toggle.expand-active"></ptc-ui-icon>`}
                                    </div>
                                </button>
                            </div>
                            <ptc-ui-collapse .collapsed=${this._collapsedSuggestions.has(department.id)}>
                                <div class="padded spacing vertical layout" style="padding-top: 0.2em">
                                    ${!templates.length
                                        ? html`
                                              <div class="small padded subtle box">
                                                  Es wurden keine Schichtvorschläge für diese Abteilung gefunden.
                                              </div>
                                          `
                                        : ""}
                                    ${templates.map((t) => {
                                        const position = app.getPosition(t.positionId!)?.position;
                                        return html`
                                            <div class="shift-template-wrapper">
                                                <div
                                                    class="small shift-template box start-aligning horizontal layout ${t.favorite
                                                        ? "favorite"
                                                        : ""}"
                                                    style="--color-highlight: ${position &&
                                                    app.getPositionColor(position)}; padding-bottom: 0.3em;"
                                                    draggable="true"
                                                    @dragstart=${(e: DragEvent) =>
                                                        this._dragstart(e, {
                                                            shiftTemplate: t,
                                                        })}
                                                >
                                                    <div class="stretch">
                                                        <div class="smaller ellipsis">${position?.name}</div>
                                                        <div class="larger">
                                                            ${t.start?.slice(0, 5)} - ${t.end?.slice(0, 5)}
                                                            ${typeof t.breakPlanned === "number"
                                                                ? html` <span class="smaller">
                                                                      <ptc-ui-icon
                                                                          name="icon.object.break"
                                                                          class="smaller"
                                                                      ></ptc-ui-icon>
                                                                      ${toDurationString(t.breakPlanned)}
                                                                  </span>`
                                                                : ""}
                                                        </div>
                                                    </div>
                                                    <ptc-ui-icon
                                                        name="icon.toggle.favorite"
                                                        ?hidden=${!t.start}
                                                        @click=${() => this._toggleFavorite(t)}
                                                    ></ptc-ui-icon>
                                                </div>
                                            </div>
                                        `;
                                    })}
                                </div>
                            </ptc-ui-collapse>
                        `
                    )}
                </ptc-scroller>
            </div>
        `;
    }

    private _renderHeader() {
        if (!this._venue || !this.dateRange) {
            return;
        }

        const unpublishedCount = this._unpublishedCount;

        const positions = this._filteredDepartments.flatMap((d) => d.positions.map((p) => p.id));
        const issues = this._issues.filter(
            (issue) =>
                issue.date >= this.dateRange!.from &&
                issue.date < this.dateRange!.to &&
                getShiftsForIssue(issue).some(
                    (shift) =>
                        shift.positionId &&
                        positions.includes(shift.positionId) &&
                        (!this._activeTab?.time || matchesTimeFilter(shift, this._activeTab.time))
                )
        );

        const isWeekRange = inferRangeType(this.dateRange) === "week";

        return html`
            <div
                class="padded spacing center-aligning horizontal layout border-bottom header relative"
                style="z-index: 11"
            >
                <ptc-roster-tabs .activeIndex=${this._activeTabIndex} class="noprint"></ptc-roster-tabs>

                <div class="horizontally-padded printonly">Dienstplan <strong>${this._venue?.name}</strong></div>

                <div class="stretch"></div>

                <ptc-date-range-picker
                    @range-selected=${(e: CustomEvent<DateRange>) =>
                        this.go(null, { ...this.router.params, ...e.detail })}
                    .range=${this.dateRange}
                    maxDays=${35}
                ></ptc-date-range-picker>

                <div class="stretch noprint"></div>

                ${unpublishedCount
                    ? html`
                          <button
                              title="Änderungen verwalten"
                              class="orange slim transparent noprint"
                              ?disabled=${!app.hasPermission(`manage.roster.publish`)}
                          >
                              <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon>
                              ${unpublishedCount}
                          </button>
                          <ptc-popover class="unpadded" style="width: 35em">
                              <ptc-roster-changes
                                  .timeEntries=${this._unpublishedEntries}
                                  @publish=${this._publishChanges}
                                  @undo=${this._undoChanges}
                              ></ptc-roster-changes>
                          </ptc-popover>
                      `
                    : html`
                          <button class="green slim transparent noprint" disabled>
                              <div class="horizontal layout">
                                  <ptc-ui-icon name="icon.status.success"></ptc-ui-icon>
                              </div>
                          </button>
                      `}

                <button
                    title="Probleme anzeigen"
                    class="${issues.length ? "red" : ""} transparent slim noprint"
                    ?disabled=${!issues.length}
                >
                    <ptc-ui-icon name="icon.status.warning"></ptc-ui-icon>
                    ${issues.length}
                </button>

                <ptc-popover class="unpadded" id="issuePopover">
                    <div class="red colored-text semibold padded border-bottom">
                        <div class="half-padded">
                            <ptc-ui-icon name="icon.status.warning"></ptc-ui-icon> ${issues.length} Probleme
                        </div>
                    </div>
                    <ptc-scroller style="max-height: 80vh">
                        ${issues.map(
                            (issue) => html`
                                <ptc-issue
                                    class="small border-bottom click"
                                    .issue=${issue}
                                    @click=${() => this._goToIssue(issue)}
                                ></ptc-issue>
                            `
                        )}
                    </ptc-scroller>
                </ptc-popover>

                <button title="Dienstplan teilen" class="slim transparent noprint">
                    <ptc-ui-icon name="icon.action.share"></ptc-ui-icon>
                </button>

                <ptc-popover class="unpadded" style="width: 28em;">
                    <div class="padded border-bottom semibold center-aligning spacing horizontal layout">
                        <div class="stretch half-padded">
                            <ptc-ui-icon name="icon.action.share"></ptc-ui-icon> Dienstplan Teilen
                        </div>
                    </div>

                    <div class="padded">
                        ${this._publicRosterUrlWithFiltersTask.render({
                            pending: () => html`
                                <div class="padded center-aligning center-justifying vertical layout">
                                    <ptc-ui-spinner active></ptc-ui-spinner>
                                </div>
                            `,
                            complete: (publicRosterUrl) =>
                                publicRosterUrl
                                    ? html`
                                          ${unpublishedCount
                                              ? html`
                                                    <div class="small padded margined orange box">
                                                        <ptc-ui-icon name="icon.status.warning"></ptc-ui-icon
                                                        ><strong>Achtung:</strong> Es liegen
                                                        <strong>${unpublishedCount}</strong> nicht veröffentlichte
                                                        Änderungen vor! Unveröffentlichte Änderungen erscheinen nicht im
                                                        geteilten Dienstplan!
                                                    </div>
                                                `
                                              : ""}

                                          <div class="horizontally-margined padded">
                                              <div class="tiny semibold faded bottom-margined">
                                                  <ptc-ui-icon name="icon.action.link"></ptc-ui-icon> Teilbarer Link:
                                              </div>
                                              <div class="small blue colored-text" style="word-break: break-all;">
                                                  <a href="${publicRosterUrl}" target="_blank">${publicRosterUrl}</a>
                                              </div>
                                          </div>

                                          <div
                                              class="small top-margined horizontally-margined horizontal evenly stretching layout"
                                          >
                                              <button
                                                  class="transparent"
                                                  type="button"
                                                  @click=${() => this._print(publicRosterUrl)}
                                                  ?hidden=${appMode === "embedded"}
                                              >
                                                  <ptc-ui-icon name="icon.action.print"></ptc-ui-icon>
                                                  Drucken
                                              </button>

                                              <button
                                                  class="transparent"
                                                  type="button"
                                                  @click=${() => navigator.clipboard.writeText(publicRosterUrl)}
                                              >
                                                  <ptc-ui-icon name="icon.action.copy-to-clipboard"></ptc-ui-icon>
                                                  Kopieren
                                              </button>

                                              <button
                                                  class="transparent nowrap"
                                                  type="button"
                                                  @click=${() => this._shareViaMessage(publicRosterUrl)}
                                                  ?disabled=${!app.hasPermission("manage.employees.messages")}
                                              >
                                                  <ptc-ui-icon name="icon.action.send-message"></ptc-ui-icon>
                                                  Versenden
                                              </button>

                                              ${!app.hasPermission("manage.employees.messages")
                                                  ? html`
                                                        <ptc-popover
                                                            class="tooltip"
                                                            trigger="hover"
                                                            non-interactive
                                                            style="width: 15em"
                                                        >
                                                            Das Versenden von Nachrichten erfordert die Berechtigung
                                                            <strong>Mitarbeiter / Nachrichten Versenden</strong>.
                                                        </ptc-popover>
                                                    `
                                                  : ""}
                                          </div>
                                      `
                                    : "",
                        })}
                    </div>
                </ptc-popover>

                <button title="Anzeige" class="slim transparent noprint">
                    <ptc-ui-icon name="icon.action.configure-view"></ptc-ui-icon>
                </button>

                <ptc-popover style="width: 20em" class="unpadded">
                    <div class="padded border-bottom semibold center-aligning spacing horizontal layout">
                        <div class="stretch half-padded">
                            <ptc-ui-icon name="icon.action.configure-view"></ptc-ui-icon> Anzeige
                        </div>
                        <div class="small tabs">
                            <button
                                @click=${() => app.updateSettings({ rosterDisplayMode: "regular" })}
                                ?active=${app.settings.rosterDisplayMode === "regular"}
                                class="slim"
                                title="Normal"
                            >
                                <ptc-ui-icon
                                    name=${activeIcon(
                                        "icon.toggle.density-normal",
                                        app.settings.rosterDisplayMode === "regular"
                                    )}
                                ></ptc-ui-icon>
                            </button>
                            <button
                                @click=${() => app.updateSettings({ rosterDisplayMode: "compact" })}
                                ?active=${app.settings.rosterDisplayMode === "compact"}
                                class="slim"
                                title="Kompakt"
                            >
                                <ptc-ui-icon
                                    name=${activeIcon(
                                        "icon.toggle.density-compact",
                                        app.settings.rosterDisplayMode === "compact"
                                    )}
                                ></ptc-ui-icon>
                            </button>
                            <button
                                @click=${() => app.updateSettings({ rosterDisplayMode: "minimal" })}
                                ?active=${app.settings.rosterDisplayMode === "minimal"}
                                class="slim"
                                title="Minimal"
                            >
                                <ptc-ui-icon
                                    name=${activeIcon(
                                        "icon.toggle.density-minimal",
                                        app.settings.rosterDisplayMode === "minimal"
                                    )}
                                ></ptc-ui-icon>
                            </button>
                        </div>
                    </div>

                    <div class="half-padded">
                        <ptc-checkbox-button
                            buttonClass="slim transparent"
                            .checked=${live(app.settings.rosterDisplayTargets)}
                            @click=${() =>
                                app.updateSettings({ rosterDisplayTargets: !app.settings.rosterDisplayTargets })}
                            .label=${html`<ptc-ui-icon name="icon.planning.staffing-target"></ptc-ui-icon>
                                Stundenvorgaben`}
                            ?disabled=${app.settings.rosterDisplayMode === "minimal"}
                        >
                        </ptc-checkbox-button>

                        <ptc-checkbox-button
                            buttonClass="slim transparent"
                            .checked=${live(app.settings.rosterDisplayUnassigned)}
                            @click=${() =>
                                app.updateSettings({ rosterDisplayUnassigned: !app.settings.rosterDisplayUnassigned })}
                            .label=${html`<ptc-ui-icon name="icon.status.unassigned"></ptc-ui-icon> Nicht Zugewiesen`}
                            ?disabled=${app.settings.rosterDisplayMode === "minimal"}
                        >
                        </ptc-checkbox-button>

                        <ptc-checkbox-button
                            buttonClass="slim transparent"
                            .checked=${live(app.settings.rosterDisplayCosts)}
                            @click=${() => app.updateSettings({ rosterDisplayCosts: !app.settings.rosterDisplayCosts })}
                            .label=${html`<ptc-ui-icon name="icon.object.revenue-overview"></ptc-ui-icon>
                                Kostenanalyse`}
                            ?hidden=${!app.hasPermission(`manage.roster.costs`)}
                        >
                        </ptc-checkbox-button>

                        <ptc-checkbox-button
                            buttonClass="slim transparent"
                            .checked=${live(app.settings.rosterMirrorDepartments)}
                            @click=${() =>
                                app.updateSettings({ rosterMirrorDepartments: !app.settings.rosterMirrorDepartments })}
                            .label=${html`<ptc-ui-icon name="icon.action.duplicate"></ptc-ui-icon> Schichten Spiegeln`}
                        >
                        </ptc-checkbox-button>

                        <ptc-checkbox-button
                            buttonClass="slim transparent"
                            .checked=${live(app.settings.rosterDisplayAvailabilities)}
                            @click=${() =>
                                app.updateSettings({
                                    rosterDisplayAvailabilities: !app.settings.rosterDisplayAvailabilities,
                                })}
                            .label=${html`<ptc-ui-icon name="icon.planning.availability"></ptc-ui-icon>
                                Verfügbarkeiten`}
                        >
                        </ptc-checkbox-button>

                        <ptc-checkbox-button
                            buttonClass="slim transparent"
                            .checked=${live(app.settings.rosterDisplayTimeBalances)}
                            @click=${() =>
                                app.updateSettings({
                                    rosterDisplayTimeBalances: !app.settings.rosterDisplayTimeBalances,
                                })}
                            .label=${html`<ptc-ui-icon name="icon.time-entry.adjustment"></ptc-ui-icon> Überst.
                                Vorher/Nachher`}
                        >
                        </ptc-checkbox-button>

                        <ptc-checkbox-button
                            buttonClass="slim transparent"
                            .checked=${live(app.settings.rosterDisplayNotes)}
                            @click=${() =>
                                app.updateSettings({
                                    rosterDisplayNotes: !app.settings.rosterDisplayNotes,
                                })}
                            .label=${html`<ptc-ui-icon name="icon.object.date"></ptc-ui-icon> Kalendereinträge`}
                        >
                        </ptc-checkbox-button>

                        <ptc-checkbox-button
                            buttonClass="slim transparent"
                            .checked=${live(app.settings.rosterDisplayWeather)}
                            @click=${async () => {
                                await app.updateSettings({
                                    rosterDisplayWeather: !app.settings.rosterDisplayWeather,
                                });
                                this._loadWeatherPredictions();
                            }}
                            .label=${html`<ptc-ui-icon name="icon.object.weather"></ptc-ui-icon> Wetter`}
                        >
                        </ptc-checkbox-button>

                        <ptc-checkbox-button
                            buttonClass="slim transparent"
                            .checked=${live(app.settings.rosterDisplaySuggestions)}
                            @click=${() =>
                                app.updateSettings({
                                    rosterDisplaySuggestions: !app.settings.rosterDisplaySuggestions,
                                })}
                            .label=${html`<ptc-ui-icon name="icon.toggle.suggestions"></ptc-ui-icon> Vorschläge &
                                Favoriten`}
                        >
                        </ptc-checkbox-button>
                    </div>
                </ptc-popover>

                <button title="Vorlagen" class="slim transparent noprint">
                    <ptc-ui-icon name="icon.action.apply-template"></ptc-ui-icon>
                </button>

                <ptc-popover class="unpadded" style="width: 25em" id="rosterTemplatesPopover">
                    <div class="padded border-bottom semibold center-aligning spacing horizontal layout">
                        <div class="stretch half-padded">
                            <ptc-ui-icon name="icon.action.apply-template"></ptc-ui-icon> Vorlagen
                        </div>
                        <div>
                            <button
                                class="small slim transparent"
                                type="button"
                                @click=${this._createRosterTemplate}
                                ?disabled=${!isWeekRange}
                            >
                                <ptc-ui-icon name="icon.action.add"></ptc-ui-icon>
                            </button>
                        </div>

                        ${!isWeekRange
                            ? html`
                                  <ptc-popover trigger="hover" class="tooltip">
                                      <div class="orange colored-text text-left-aligning">
                                          <ptc-ui-icon name="icon.status.warning"></ptc-ui-icon> Das Erstellen von
                                          Vorlagen ist nur in der <strong>Wochenansicht</strong> möglich (Montag bis
                                          Sonntag). Wechseln Sie in die Wochenansicht um fortzufahren.
                                      </div>
                                  </ptc-popover>
                              `
                            : ""}
                    </div>

                    <ptc-scroller style="max-height: 80vh;">
                        ${!this._venue.rosterTemplates.length
                            ? html`<div class="double-padded subtle">
                                  Klicken Sie auf den
                                  <ptc-ui-icon name="icon.action.add:active"></ptc-ui-icon> button, um eine neue Vorlage
                                  zu erstellen.
                              </div>`
                            : ""}
                        ${this._venue.rosterTemplates
                            .filter((template) =>
                                template.shifts.every((shift) => {
                                    const position = app.getPosition(shift.position)?.position;
                                    return app.hasAccess({ position });
                                })
                            )
                            .map((template) => {
                                const departments = this._getTemplateDepartments(template);
                                return html`
                                    <div
                                        class="double-padded border-bottom click relative"
                                        @click=${() => this._selectRosterTemplate(template)}
                                    >
                                        <div class="bottom-margined ellipsis semibold">${template.name}</div>
                                        <div class="tiny pills">
                                            ${departments.map(
                                                ({ name, color, count }) => html`
                                                    <div
                                                        class="pill"
                                                        style="--color-highlight: ${colors[color] || color}"
                                                    >
                                                        ${name}
                                                        <span class="bold">${count}</span>
                                                    </div>
                                                `
                                            )}
                                        </div>
                                        <button
                                            class="small slim transparent margined top right reveal-on-parent-hover absolute"
                                            @click=${(e: MouseEvent) => {
                                                e.stopPropagation();
                                                void this._deleteRosterTemplate(template);
                                            }}
                                        >
                                            <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                                        </button>
                                    </div>
                                `;
                            })}
                    </ptc-scroller>
                </ptc-popover>

                <button title="Einstellungen" class="slim transparent noprint">
                    <ptc-ui-icon name="icon.feature.settings"></ptc-ui-icon>
                </button>

                <ptc-popover class="popover-menu">
                    <button @click=${this._unassignAll} ?hidden=${!this._enabledFeatures.rosterAutoAssign}>
                        <ptc-ui-icon name="icon.status.unassigned"></ptc-ui-icon> Schichtzuweisungen aufheben
                    </button>

                    <div>
                        <button
                            @click=${this._showAutoAssign}
                            ?hidden=${!this._enabledFeatures.rosterAutoAssign}
                            ?disabled=${!isWeekRange}
                        >
                            <ptc-ui-icon name="icon.action.auto-assign"></ptc-ui-icon> Schichten Automatisch Zuweisen
                        </button>
                    </div>
                    ${!isWeekRange && this._enabledFeatures.rosterAutoAssign
                        ? html`
                              <ptc-popover trigger="hover" class="tooltip">
                                  <div class="orange colored-text text-left-aligning">
                                      <ptc-ui-icon name="icon.status.warning"></ptc-ui-icon> Die automatische
                                      Schichtzuweisung ist nur in der <strong>Wochenansicht</strong> möglich (Montag bis
                                      Sonntag). Wechseln Sie in die Wochenansicht um fortzufahren.
                                  </div>
                              </ptc-popover>
                          `
                        : ""}

                    <button @click=${() => this._clearRosterDialog.show()}>
                        <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon> Dienstplan Leeren
                    </button>
                </ptc-popover>
            </div>
        `;
    }

    private _highlightedEmployeeId: number | null = null;

    private async _highlightEmployee(employeeId: number, departmentId?: number) {
        const employeeRow = findEmployeeRow(this.renderRoot, employeeId, departmentId) as HTMLDivElement | null;
        if (!employeeRow) {
            return;
        }

        this._highlightedEmployeeId = employeeId;
        employeeRow.classList.add("highlight");
        await this._centerHighlightedRow(employeeRow);

        setTimeout(() => {
            employeeRow.classList.remove("highlight");
        }, 2000);
    }

    // Async layout shift (avatars loading, rows mounting at real height) moves the target after a
    // single scrollIntoView, so re-center until the row stops moving — converges in 1-2 passes.
    private async _centerHighlightedRow(row: HTMLElement) {
        const main = this._main;
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        if (!main) {
            return;
        }
        const offsetFromCenter = () => {
            const m = main.getBoundingClientRect();
            const r = row.getBoundingClientRect();
            return r.top + r.height / 2 - (m.top + m.height / 2);
        };
        for (let pass = 0; pass < 8; pass++) {
            // Sequential by design: wait for layout to settle, re-measure, correct, repeat.
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, 150));
            if (Math.abs(offsetFromCenter()) < 3) {
                break;
            }
            row.scrollIntoView({ behavior: "instant" as ScrollBehavior, block: "center" });
        }
    }

    render() {
        if (!app.accessibleVenues.length) {
            return html`
                <div class="fullbleed spacing centering vertical layout">
                    <div class="large bold">Nicht so schnell!</div>
                    <div class="padded" style="max-width: 30em;">
                        Bevor Sie mit der Dienstplanung beginnen können müssen Sie zunächst Ihre Standorte und
                        Abteilungen einrichten!
                    </div>
                    <button @click=${() => this.go("settings/venues")}>
                        Arbeitsbereiche Definieren <ptc-ui-icon name="icon.navigation.forward"></ptc-ui-icon>
                    </button>
                </div>
            `;
        }

        if (!this._venue || !this._data) {
            return html`
                <div class="fullbleed center-aligning center-justifying vertical layout scrim">
                    <ptc-ui-spinner active></ptc-ui-spinner>
                </div>
            `;
        }

        return html`
            <div
                class="fullbleed vertical layout stretch main-outer ${app.settings.rosterDisplayMode} ${this._data.dates
                    .length > 9
                    ? "large-range"
                    : ""} collapse"
                style="--roster-row-height: ${this._entryHeight}em;"
            >
                ${this._renderHeader()} ${this._renderRosterTemplatePreview()}

                <div class="stretch horizontal layout collapse">
                    <div
                        class="main stretch ${app.settings.rosterDisplayCosts ? "show-charts" : ""} ${app.settings
                            .rosterDisplayTargets
                            ? "show-targets"
                            : ""} collapse"
                    >
                        ${this._renderDayHeader()} ${this._data.departments.map((dep) => this._renderDepartment(dep))}
                        ${this._renderCosts()}
                    </div>

                    ${this._displayAutoAssignMenu
                        ? this._renderAutoAssignMenu()
                        : this._activeDate
                          ? this._renderDayMenu()
                          : this._renderGlobalShiftSuggestions()}
                </div>
            </div>

            <ptc-dialog id="clearRosterDialog">
                <div class="padded center-aligning horizontal layout border-bottom">
                    <div class="half-padded semibold stretch">
                        <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon> Dienstplan Leeren
                    </div>
                    <button class="slim transparent" @click=${() => this._clearRosterDialog.hide()}>
                        <ptc-ui-icon name="icon.action.close"></ptc-ui-icon>
                    </button>
                </div>

                <form
                    class="padded spacing vertical layout"
                    @submit=${this._clearTimeEntries}
                    @keydown=${(e: Event) => e.stopPropagation()}
                >
                    <div class="subtle padded box">
                        <ptc-ui-icon name="icon.status.info"></ptc-ui-icon> <strong>Hinweis</strong>: Es werden nur jene
                        Schichten gelöscht, die den <strong>Filterkriterien</strong> des aktuellen Tabs entsprechen.
                        <a href="https://hilfe.pentacode.app/handbuch/dienstplan/#dienstplan-leeren" target="_blank">
                            Mehr Infos ➜
                        </a>
                    </div>

                    <ptc-checkbox-button
                        .label=${html`<ptc-ui-icon name="icon.object.date"></ptc-ui-icon> Geplante Schichten`}
                        name="planned"
                        buttonClass="slim ghost"
                        checked
                    >
                    </ptc-checkbox-button>

                    <ptc-checkbox-button
                        .label=${html`<ptc-ui-icon name="icon.status.success"></ptc-ui-icon> Abgeschlossene Schichten`}
                        name="finished"
                        buttonClass="slim ghost"
                    >
                    </ptc-checkbox-button>

                    <div class="vertical layout">
                        <button class="negative">Dienstplan Leeren</button>
                    </div>
                </form>
            </ptc-dialog>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
